import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  ExpressCheckoutElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { FiCreditCard, FiCheck, FiArrowLeft, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import api from '../../services/api';
import { useServerConfig } from '../../hooks/useServerConfig';
import logger from '../../utils/logger';
import styles from './PaymentForm.module.css';

// Payment form component
function PaymentFormContent({ onClientSecretChange, clientSecretFromParent, preservedAppliedPromo, setPreservedAppliedPromo }) {
  const { courseId, courseIds, productIds } = useParams();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Pre-fetch CSRF token on mount to prevent "CSRF token is invalid or expired" errors
  useEffect(() => {
    const prefetchCSRFToken = async () => {
      try {
        await api.fetchCSRFToken();
      } catch (error) {
        logger.warn('Failed to pre-fetch CSRF token', { error });
      }
    };
    prefetchCSRFToken();
  }, []);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [promoError, setPromoError] = useState(null);
  const [promoInfo, setPromoInfo] = useState(null); // FIX: Info message for promo code application (e.g., when replacing bulk pricing)
  const [success, setSuccess] = useState(false);
  const [bulkPricing, setBulkPricing] = useState(null);
  const [promoCode, setPromoCode] = useState('');
  // FIX: Use preserved promo code from parent if available (survives remounts)
  const [appliedPromo, setAppliedPromo] = useState(preservedAppliedPromo || null);
  
  // Sync appliedPromo when preserved value changes (from parent)
  // Note: We intentionally don't include appliedPromo in dependencies to avoid infinite loops
  // This effect only runs when preservedAppliedPromo changes from parent
  useEffect(() => {
    if (preservedAppliedPromo && preservedAppliedPromo !== appliedPromo) {
      setAppliedPromo(preservedAppliedPromo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preservedAppliedPromo]);
  
  // Update preserved promo code in parent whenever it changes
  useEffect(() => {
    if (setPreservedAppliedPromo) {
      setPreservedAppliedPromo(appliedPromo);
    }
  }, [appliedPromo, setPreservedAppliedPromo]);
  const [clientSecret, setClientSecret] = useState(clientSecretFromParent || null);
  const [paymentIntentCreated, setPaymentIntentCreated] = useState(!!clientSecretFromParent);
  // Track if user has clicked "Proceed to Payment" - payment intent will be created on click
  const [showPaymentForm, setShowPaymentForm] = useState(!!clientSecretFromParent);
  const [creatingPaymentIntent, setCreatingPaymentIntent] = useState(false);
  // FIX: Track if we've already attempted initial payment intent creation to prevent loops on remount
  const initialPaymentIntentAttemptedRef = useRef(!!clientSecretFromParent);
  const [promoLoading, setPromoLoading] = useState(false); // FIX #13: Loading state for promo code
  const [bulkPricingLoading, setBulkPricingLoading] = useState(false); // FIX #6 & #14: Loading state for bulk pricing calculation
  // FIX #8: Store initial course prices for validation
  const initialCoursePricesRef = useRef(new Map()); // courseId -> price
  // Use refs to track previous values and prevent unnecessary re-renders
  const prevBulkPricingRef = useRef(null);
  const prevAppliedPromoRef = useRef(null);
  // FIX: Use refs to access current state values in setTimeout callbacks (avoid stale closures)
  const appliedPromoRef = useRef(appliedPromo);
  const bulkPricingRef = useRef(bulkPricing);
  const coursesRef = useRef(courses);
  const productIdsRef = useRef(productIds); // FIX: Track productIds to ensure it's available in callbacks
  
  // Keep refs in sync with state
  useEffect(() => {
    appliedPromoRef.current = appliedPromo;
  }, [appliedPromo]);
  
  useEffect(() => {
    bulkPricingRef.current = bulkPricing;
  }, [bulkPricing]);
  
  useEffect(() => {
    coursesRef.current = courses;
  }, [courses]);
  
  useEffect(() => {
    productIdsRef.current = productIds;
  }, [productIds]);
  const lastPaymentIntentAttemptRef = useRef(null); // Track last attempt to prevent rapid retries
  const paymentIntentCreationLockRef = useRef(false); // Mutex to prevent concurrent payment intent creation
  const abortControllerRef = useRef(null); // FIX #13: AbortController for cancelling API calls on unmount
  const isFetchingRef = useRef(false); // Track if we're currently fetching to prevent concurrent fetches
  const lastFetchedParamsRef = useRef(null); // Track last fetched params to prevent refetch loops
  const isRestoringBulkPricingRef = useRef(false); // Track if we're restoring bulk pricing to prevent discount change detection
  // FIX #31: Rate limiting for API calls - track last call times
  // FIX #9: Use sliding window approach - only keep recent timestamps
  const apiCallTimestampsRef = useRef({
    createPaymentIntent: 0,
    applyPromoCode: 0,
    removePromoCode: 0,
    calculateBulkPricing: 0
  });
  const RATE_LIMIT_DELAY = 1000; // Minimum 1 second between same-type API calls
  const RATE_LIMIT_CLEANUP_INTERVAL = 60000; // Clean up old timestamps every 60 seconds
  
  // FIX #9: Cleanup old rate limiting timestamps periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      const timestamps = apiCallTimestampsRef.current;
      
      // Clear timestamps older than 5 minutes (no longer relevant for rate limiting)
      Object.keys(timestamps).forEach(key => {
        if (timestamps[key] > 0 && (now - timestamps[key]) > 300000) {
          timestamps[key] = 0;
        }
      });
    }, RATE_LIMIT_CLEANUP_INTERVAL);
    
    return () => clearInterval(cleanupInterval);
  }, []);
  // FIX #1: Use refs to avoid stale closures in setTimeout callbacks
  const processingRef = useRef(processing);
  const paymentIntentCreatedRef = useRef(paymentIntentCreated);
  
  // Keep refs in sync with state
  useEffect(() => {
    processingRef.current = processing;
  }, [processing]);
  
  useEffect(() => {
    paymentIntentCreatedRef.current = paymentIntentCreated;
  }, [paymentIntentCreated]);



  // Fetch course/product information
  // FIX: Use sessionStorage to track fetched params - persists across remounts
  // This prevents refetch when Elements remounts due to clientSecret changes
  useEffect(() => {
    // Create a unique key for the current params
    const paramsKey = `${courseId || ''}_${courseIds || ''}_${productIds || ''}`;
    const storageKey = `paymentForm_fetched_${paramsKey}`;
    const coursesDataKey = `paymentForm_courses_${paramsKey}`;
    
    // CRITICAL: Check if we just fetched for these exact params (prevents immediate refetch)
    if (lastFetchedParamsRef.current === paramsKey && !isFetchingRef.current) {
      logger.debug('Just fetched for these params, skipping', { paramsKey });
      return;
    }
    
    // CRITICAL: Early return if already fetching - prevent concurrent fetches
    if (isFetchingRef.current) {
      logger.debug('Fetch already in progress, skipping', { paramsKey });
      return;
    }
    
    // FIX: Skip if data already fetched for these exact params (prevents refetch on remounts)
    // Check sessionStorage first - if it exists, we've already fetched for these params
    // We intentionally don't include courses.length in deps - we use sessionStorage to prevent loops
    // CRITICAL: On remount, courses will be empty, but sessionStorage persists
    // If we've already fetched, skip to prevent infinite loops
    const alreadyFetched = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(storageKey) === 'true';
    
    if (alreadyFetched) {
      // FIX: Always try to restore from storage first - this handles remounts gracefully
      const storedCoursesData = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(coursesDataKey);
      
      if (storedCoursesData) {
        // We have stored data - restore it
        try {
          const parsedCourses = JSON.parse(storedCoursesData);
          if (Array.isArray(parsedCourses) && parsedCourses.length > 0) {
            // Only restore if courses state is empty (prevents overwriting if already set)
            if (courses.length === 0) {
              setCourses(parsedCourses);
              // CRITICAL: Update ref immediately so payment intent creation can use it
              coursesRef.current = parsedCourses;
            }
            setLoading(false);
            
            // CRITICAL: Only restore bulk pricing if we DON'T already have a payment intent
            // If we have a payment intent, the bulk pricing should already be in state (React state persists across remounts)
            // Restoring it would trigger the discount change useEffect unnecessarily
            const hasPaymentIntent = clientSecretFromParent || clientSecret || paymentIntentCreatedRef.current;
            
            // Define these variables outside the conditional blocks so they're accessible later
            const bulkPricingKey = `paymentForm_bulkPricing_${paramsKey}`;
            const storedBulkPricing = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(bulkPricingKey);
            
            if (!hasPaymentIntent) {
              // No payment intent yet - safe to restore bulk pricing
              if (storedBulkPricing && !bulkPricing) {
                try {
                  const parsedBulkPricing = JSON.parse(storedBulkPricing);
                  if (parsedBulkPricing && parsedBulkPricing.bulkPricing) {
                    // Set both state and ref to prevent payment intent recreation
                    // CRITICAL: Set flag and ref BEFORE state to prevent discount change detection
                    isRestoringBulkPricingRef.current = true;
                    prevBulkPricingRef.current = parsedBulkPricing;
                    setBulkPricing(parsedBulkPricing);
                    // Reset flag after state update completes
                    setTimeout(() => {
                      isRestoringBulkPricingRef.current = false;
                    }, 0);
                    logger.debug('Bulk pricing restored from sessionStorage', { 
                      discount: parsedBulkPricing.pricing?.discountAmount 
                    });
                  }
                } catch (e) {
                  logger.debug('Failed to parse stored bulk pricing', { error: e });
                  sessionStorage.removeItem(bulkPricingKey);
                }
              }
            } else {
              // Payment intent exists - bulk pricing should be in state already
              // Only restore if state is empty (shouldn't happen, but safety check)
              if (!bulkPricing) {
                // Reuse bulkPricingKey and storedBulkPricing from above
                if (storedBulkPricing) {
                  try {
                    const parsedBulkPricing = JSON.parse(storedBulkPricing);
                    if (parsedBulkPricing && parsedBulkPricing.bulkPricing) {
                      // CRITICAL: Set flag and ref BEFORE setting state to prevent change detection
                      isRestoringBulkPricingRef.current = true;
                      prevBulkPricingRef.current = parsedBulkPricing;
                      setBulkPricing(parsedBulkPricing);
                      // Reset flag after state update completes
                      setTimeout(() => {
                        isRestoringBulkPricingRef.current = false;
                      }, 0);
                      logger.debug('Bulk pricing restored from sessionStorage (payment intent exists)', { 
                        discount: parsedBulkPricing.pricing?.discountAmount 
                      });
                    }
                  } catch (e) {
                    logger.debug('Failed to parse stored bulk pricing', { error: e });
                    sessionStorage.removeItem(bulkPricingKey);
                  }
                }
              }
            }
            
            // Only calculate bulk pricing if we don't have it in state or sessionStorage
            // Reuse hasPaymentIntent from above
            if (!hasPaymentIntent) {
              
              // CRITICAL: Only calculate bulk pricing if not already calculated and not in sessionStorage
              // This prevents recalculation on every remount
              // FIX: Only calculate bulk pricing for courses, not products
              if (parsedCourses.length > 1 && !bulkPricing && !storedBulkPricing && !bulkPricingLoading && !productIds) {
                const calculateBulkPricingForRestored = async () => {
                  try {
                    setBulkPricingLoading(true);
                    const courseIdsForBulk = parsedCourses.map(course => parseInt(course.id));
                    const now = Date.now();
                    const lastCallTime = apiCallTimestampsRef.current.calculateBulkPricing;
                    if (lastCallTime && (now - lastCallTime) < RATE_LIMIT_DELAY) {
                      setBulkPricingLoading(false);
                      return;
                    }
                    apiCallTimestampsRef.current.calculateBulkPricing = now;
                    
                    logger.debug('Calculating bulk pricing for restored courses', { courseIdsForBulk });
                    const bulkResponse = await api.calculateBulkPricing(courseIdsForBulk);
                    if (bulkResponse.success && bulkResponse.bulkPricing) {
                      // CRITICAL: Not restoring - this is a fresh calculation, so update prevRef
                      prevBulkPricingRef.current = bulkResponse;
                      setBulkPricing(bulkResponse);
                      // Store bulk pricing in sessionStorage for future remounts
                      if (typeof sessionStorage !== 'undefined') {
                        sessionStorage.setItem(bulkPricingKey, JSON.stringify(bulkResponse));
                      }
                      logger.info('Bulk pricing applied for restored courses', { 
                        discount: bulkResponse.pricing.discountAmount,
                        finalPrice: bulkResponse.pricing.finalPrice
                      });
                    } else {
                      setBulkPricing(null);
                    }
                  } catch (error) {
                    logger.error('Error calculating bulk pricing for restored courses', { error });
                    setBulkPricing(null);
                  } finally {
                    setBulkPricingLoading(false);
                  }
                };
                calculateBulkPricingForRestored();
              }
            } else {
              // Payment intent already exists - don't restore bulk pricing to avoid triggering useEffects
              logger.debug('Skipping bulk pricing restoration - payment intent already exists', {
                hasClientSecret: !!clientSecret,
                hasClientSecretFromParent: !!clientSecretFromParent,
                paymentIntentCreated: paymentIntentCreatedRef.current
              });
            }
            
            // CRITICAL: Return early to prevent fetch - we already have the data
            return;
          } else {
            // Invalid parsed data
            sessionStorage.removeItem(coursesDataKey);
          }
        } catch (e) {
          // Invalid stored data - clear it but don't refetch (might be in progress)
          sessionStorage.removeItem(coursesDataKey);
        }
      }
      
      // If we have courses in state, we're good - don't refetch
      if (courses.length > 0) {
        setLoading(false);
        return;
      }
      
      // If we get here, we have the flag but no data - this means a previous fetch failed
      // Clear the flag and error state, then refetch
      const storedCoursesDataCheck = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(coursesDataKey);
      logger.debug('SessionStorage flag exists but no courses data found - clearing and refetching', {
        paramsKey,
        hasStoredData: !!storedCoursesDataCheck,
        coursesLength: courses.length 
      });
      // Clear the flag and error so we can refetch
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(storageKey);
        sessionStorage.removeItem(coursesDataKey);
      }
      setError(null); // Clear any previous error
      // Continue to fetch below (don't return)
    }
    
    // FIX #13: Create AbortController for this effect to cancel API calls on unmount
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    const fetchData = async () => {
      // CRITICAL: Set fetching flag to prevent concurrent fetches
      if (isFetchingRef.current) {
        logger.debug('Fetch already in progress, skipping duplicate call', { paramsKey });
        return;
      }
      isFetchingRef.current = true;
      setLoading(true);
      
      try {
        if (productIds) {
          const identifiers = productIds.split(',');
          
          let productsData;
          try {
            const productPromises = identifiers.map(id => {
              const productId = id.trim();
              return api.getAdditionalProduct(productId)
                .then(response => {
                  // API returns { success: true, product: {...} }
                  // Check for the expected structure: { success: true, product: {...} }
                  if (response && response.success === true && response.product) {
                    return { success: true, product: response.product, productId };
                  } 
                  // Handle case where product is returned directly (shouldn't happen but be defensive)
                  else if (response && (response.id || response.name)) {
                    return { success: true, product: response, productId };
                  } 
                  // Handle case where response structure is different
                  else {
                    return { 
                      success: false, 
                      error: 'Unexpected response format from server',
                      productId,
                      status: 500,
                      response: response // Include response for debugging
                    };
                  }
                })
                .catch(error => {
                  // Return a structured error response for better handling
                  return { 
                    success: false, 
                    error: error.message || 'Product not found',
                    productId,
                    status: error.status || 500
                  };
                });
            });
            const responses = await Promise.all(productPromises);
            
            // Separate successful responses from error responses
            const errorResponses = responses.filter(r => !r || r.success === false);
            const successfulResponses = responses.filter(r => r && r.success === true && r.product);
            
            // If we have some successful responses, use them even if there are errors
            if (successfulResponses.length > 0) {
              // Extract products from successful responses
              productsData = successfulResponses.map(r => r.product).filter(product => product && (product.id || product.name));
            } else if (errorResponses.length > 0) {
              // All products failed - this is a real error
              const errorDetails = errorResponses.map(r => r?.productId || 'unknown').join(', ');
              const firstError = errorResponses[0];
              let errorMessage = firstError?.error || 'Product not found';
              
              // Provide more specific error messages based on status
              if (firstError?.status === 404) {
                errorMessage = `Product(s) with ID(s) ${errorDetails} not found. They may have been removed, are not active, or are no longer available for purchase.`;
              } else if (firstError?.status === 401 || firstError?.status === 403) {
                errorMessage = 'Authentication required. Please log in and try again.';
              } else if (!errorMessage.includes('Product')) {
                errorMessage = `Failed to fetch product information (IDs: ${errorDetails}). ${errorMessage}`;
              }
              
              const error = new Error(errorMessage);
              error.status = firstError?.status || 404;
              throw error;
            } else {
              // No responses at all - this shouldn't happen
              throw new Error('No product data received from server');
            }
          } catch (productError) {
            logger.error('Product fetch error', { productError, productIds, errorMessage: productError.message });
            // Provide more specific error message based on the error
            let errorMessage = 'Failed to load product information. Please try again.';
            if (productError.message) {
              errorMessage = productError.message;
            } else if (productError.status === 404) {
              errorMessage = 'Product not found. The product may have been removed or is no longer available.';
            } else if (productError.status === 401 || productError.status === 403) {
              errorMessage = 'Authentication required. Please log in and try again.';
            }
            setError(errorMessage);
            setLoading(false);
            // FIX: Clear any sessionStorage data for products when fetch fails
            if (typeof sessionStorage !== 'undefined') {
              const paramsKey = `${courseId || ''}_${courseIds || ''}_${productIds || ''}`;
              const storageKey = `paymentForm_fetched_${paramsKey}`;
              const coursesDataKey = `paymentForm_courses_${paramsKey}`;
              sessionStorage.removeItem(storageKey);
              sessionStorage.removeItem(coursesDataKey);
            }
            return;
          }
          
          // FIX #8: Validate that all products are active/available
          const inactiveProducts = productsData.filter(product => !product.is_active);
          if (inactiveProducts.length > 0) {
            const inactiveNames = inactiveProducts.map(p => p.name).join(', ');
            const errorMessage = `The following products are no longer available: ${inactiveNames}`;
            setError(errorMessage);
            setLoading(false);
            // FIX: Clear sessionStorage when products are inactive
            if (typeof sessionStorage !== 'undefined') {
              const paramsKey = `${courseId || ''}_${courseIds || ''}_${productIds || ''}`;
              const storageKey = `paymentForm_fetched_${paramsKey}`;
              const coursesDataKey = `paymentForm_courses_${paramsKey}`;
              sessionStorage.removeItem(storageKey);
              sessionStorage.removeItem(coursesDataKey);
            }
            return;
          }
          
          const courseLikeData = productsData.map(product => {
            return {
              id: product.id,
              name: product.name,
              code: `PROD-${product.id}`,
              description: product.description,
              price: parseFloat(product.price) || 0,
              currency: product.currency || 'USD',
              isPaidCourse: (parseFloat(product.price) || 0) > 0,
              type: 'product'
            };
          });
          
          setCourses(courseLikeData);
          if (typeof sessionStorage !== 'undefined' && courseLikeData && courseLikeData.length > 0) {
            sessionStorage.setItem(storageKey, 'true');
            // Also store the actual products data to restore on remount
            const coursesDataKey = `paymentForm_courses_${paramsKey}`;
            sessionStorage.setItem(coursesDataKey, JSON.stringify(courseLikeData));
            // CRITICAL: Mark these params as just fetched to prevent immediate refetch
            lastFetchedParamsRef.current = paramsKey;
            logger.debug('✅ Products set and sessionStorage updated', { 
              productsCount: courseLikeData.length,
              productIds: courseLikeData.map(p => p.id),
              storageKey 
            });
          }
          initialCoursePricesRef.current.clear();
          courseLikeData.forEach(product => {
            initialCoursePricesRef.current.set(product.id, parseFloat(product.price) || 0);
          });
          // FIX: Products don't use bulk pricing, so clear it and set loading to false
          setBulkPricing(null);
          setBulkPricingLoading(false);
        } else if (courseIds) {
          const identifiers = courseIds.split(',');
          const isNumeric = identifiers.every(id => /^\d+$/.test(id));
          
          let coursesData;
          
          if (isNumeric) {
            const coursePromises = identifiers.map(id => api.getCoursePricing(id));
            const responses = await Promise.all(coursePromises);
            coursesData = responses.map(response => response.course);
          } else {
            // Handle course codes
            let allCoursesResponse;
            try {
              logger.info('Calling getPublicCourses API', { identifiers });
              allCoursesResponse = await api.getPublicCourses();
              logger.info('getPublicCourses API call completed', { 
                hasResponse: !!allCoursesResponse,
                success: allCoursesResponse?.success,
                responseType: typeof allCoursesResponse,
                responseKeys: allCoursesResponse ? Object.keys(allCoursesResponse) : []
              });
            } catch (apiError) {
              logger.error('Error calling getPublicCourses API', { 
                apiError, 
                errorMessage: apiError.message, 
                errorStatus: apiError.status,
                errorResponse: apiError.response,
                errorData: apiError.response?.data,
                errorStack: apiError.stack,
                identifiers
              });
              // Handle authentication errors specifically
              if (apiError.status === 401 || apiError.status === 403) {
                // FIX: Provide more helpful message for auth errors
                throw new Error('Your session has expired. Please refresh the page and try again. If the problem persists, please log out and log back in.');
              }
              // Handle network errors
              if (apiError.isNetworkError || apiError.isTimeout) {
                throw new Error('Network error. Please check your connection and try again.');
              }
              // Generic error
              throw new Error(apiError.message || 'Failed to fetch course catalog. Please try again.');
            }
            
            // Check if response is valid
            if (!allCoursesResponse) {
              logger.error('Null response from getPublicCourses', { allCoursesResponse });
              throw new Error('Failed to fetch course catalog. The server returned no data. Please try again.');
            }
            
            if (!allCoursesResponse.success) {
              logger.error('Unsuccessful response from getPublicCourses', { 
                allCoursesResponse,
                error: allCoursesResponse.error,
                message: allCoursesResponse.message 
              });
              const errorMsg = allCoursesResponse.message || allCoursesResponse.error || 'Failed to fetch course catalog. Please try again.';
              throw new Error(errorMsg);
            }
            
            // Safely access courses array with fallback
            const allCourses = (allCoursesResponse && allCoursesResponse.courses) ? allCoursesResponse.courses : [];
            
            // FIX: Enhanced logging to diagnose catalog issues
            const catalogInfo = {
              hasResponse: !!allCoursesResponse,
              success: allCoursesResponse?.success,
              totalCourses: allCourses.length,
              identifiers,
              courseCodes: allCourses.map(c => ({ code: c.code, name: c.name, active: c.is_active })),
              allCourseCodesList: allCourses.map(c => c.code), // Simple list for easy scanning
              responseKeys: allCoursesResponse ? Object.keys(allCoursesResponse) : [],
              lookingFor: identifiers,
              foundInCatalog: identifiers.filter(code => allCourses.some(c => c.code === code))
            };
            
            logger.info('Catalog API response received', catalogInfo);
            
            if (allCourses.length === 0) {
              logger.warn('Catalog returned empty courses array', {
                response: allCoursesResponse,
                identifiers,
                message: 'This may indicate an authentication issue or the catalog is empty'
              });
            }
            
            const selectedCourses = allCourses.filter(course => {
              const matches = identifiers.includes(course.code);
              if (!matches && course.code) {
                logger.debug('Course code mismatch', { 
                  courseCode: course.code, 
                  identifiers, 
                  exactMatch: identifiers.some(id => id === course.code),
                  caseSensitive: identifiers.some(id => id.toLowerCase() === course.code?.toLowerCase())
                });
              }
              return matches;
            });
            
            logger.info('Course filtering complete', { 
              selectedCourses: selectedCourses.length,
              identifiers,
              foundCodes: selectedCourses.map(c => c.code),
              missingCodes: identifiers.filter(code => !selectedCourses.some(c => c.code === code)),
              allCourseCodes: allCourses.map(c => c.code)
            });
            
            // Check if all requested courses were found
            if (selectedCourses.length !== identifiers.length) {
              const foundCodes = selectedCourses.map(c => c.code);
              const missingCodes = identifiers.filter(code => !foundCodes.includes(code));
              
              // FIX: Provide more specific error message based on catalog state
              if (allCourses.length === 0) {
                logger.error('Catalog returned empty array', {
                  identifiers,
                  response: allCoursesResponse
                });
                throw new Error('The course catalog is currently empty. This may indicate an authentication issue or no courses are available for enrollment. Please refresh the page or contact support if the problem persists.');
              }
              logger.warn('Some courses not found in catalog', { missingCodes, foundCodes, identifiers });
              
              // FIX: Provide more helpful error message explaining why course might not be available
              const errorMsg = missingCodes.length === 1 
                ? `Course "${missingCodes[0]}" is not available for enrollment. The course may be inactive or the enrollment period has ended. Please contact support if you believe this is an error.`
                : `The following courses are not available for enrollment: ${missingCodes.join(', ')}. These courses may be inactive or their enrollment periods have ended. Please contact support if you believe this is an error.`;
              throw new Error(errorMsg);
            }
            
            // Use pricing data directly from catalog response (already includes pricing)
            // No need to make separate pricing API calls
            // FIX: Add null checks to prevent errors if course data is incomplete
            coursesData = selectedCourses.map(course => {
              if (!course || !course.id) {
                logger.error('Invalid course data in catalog response', { course });
                throw new Error('Invalid course data received from server. Please try again.');
              }
              return {
                id: course.id,
                name: course.name || 'Unnamed Course',
                description: course.description || '',
                code: course.code || '',
                instructor: course.instructor || 'TBA',
                price: course.price || 0,
                currency: course.currency || 'USD',
                isPaidCourse: course.isPaidCourse || false,
                maxStudents: course.maxStudents || null,
                startDate: course.startDate || null,
                endDate: course.endDate || null,
                is_active: course.is_active !== undefined ? course.is_active : true // FIX #8: Include active status with safe default
              };
            });
            
            // FIX #17: Validate currency consistency - all courses must use same currency
            const currencies = [...new Set(coursesData.map(c => c.currency || 'USD'))];
            if (currencies.length > 1) {
              logger.warn('Multiple currencies detected', { currencies, courses: coursesData.map(c => ({ name: c.name, currency: c.currency })) });
              // For now, log warning - backend should handle currency validation
              // In future, could show error or convert currencies
            }
            
            logger.debug('Course data from codes (using catalog pricing)', { coursesData });
          }
          
          // Validate that we have course data
          if (!coursesData || coursesData.length === 0) {
            throw new Error('No courses found with the provided codes');
          }
          
          // FIX #8: Validate that all courses are active/available (allow AQ101 and FWL101 even if inactive)
          const allowedInactiveCourses = ['AQ101', 'FWL101'];
          const inactiveCourses = coursesData.filter(course => 
            course.is_active === false && !allowedInactiveCourses.includes(course.code)
          );
          if (inactiveCourses.length > 0) {
            const inactiveNames = inactiveCourses.map(c => c.name || c.code).join(', ');
            throw new Error(`The following courses are no longer available: ${inactiveNames}`);
          }
          
          // Set courses state first
          setCourses(coursesData);
          // FIX: Only set sessionStorage if we actually have courses data
          // React state updates are async, so we check coursesData length, not courses state
          if (typeof sessionStorage !== 'undefined' && coursesData && coursesData.length > 0) {
            sessionStorage.setItem(storageKey, 'true'); // Mark as fetched for these params (persists across remounts)
            // Also store the actual courses data to restore on remount
            const coursesDataKey = `paymentForm_courses_${paramsKey}`;
            sessionStorage.setItem(coursesDataKey, JSON.stringify(coursesData));
            // CRITICAL: Mark these params as just fetched to prevent immediate refetch
            lastFetchedParamsRef.current = paramsKey;
          }
          
          // FIX #8: Store initial course prices for validation
          initialCoursePricesRef.current.clear();
          coursesData.forEach(course => {
            initialCoursePricesRef.current.set(course.id, parseFloat(course.price) || 0);
          });
          
          // FIX #14: Wait for bulk pricing before creating initial payment intent
          // FIX #35: Only calculate bulk pricing when needed (more than 1 course)
          // Check for bulk pricing if more than 1 course
          // Do this after setting courses to ensure we have the latest course data
          // FIX: Only calculate bulk pricing for courses, not products
          // Products should not use bulk pricing - they use their own pricing logic
          if (coursesData.length > 1 && !productIds) {
            try {
              // Ensure course IDs are integers (backend expects integers)
              const courseIdsForBulk = coursesData.map(course => parseInt(course.id));
              // FIX #31: Rate limiting - prevent rapid API calls
              const now = Date.now();
              const lastCallTime = apiCallTimestampsRef.current.calculateBulkPricing;
              if (lastCallTime && (now - lastCallTime) < RATE_LIMIT_DELAY) {
                logger.debug('Bulk pricing calculation rate limited, skipping');
                // Still set loading to false since we're skipping
                setBulkPricingLoading(false);
                return;
              }
              apiCallTimestampsRef.current.calculateBulkPricing = now;
              
              logger.debug('Calculating bulk pricing', { 
                courseIdsForBulk, 
                courseCount: courseIdsForBulk.length,
                courses: coursesData.map(c => ({ id: c.id, name: c.name, price: c.price }))
              });
              const bulkResponse = await api.calculateBulkPricing(courseIdsForBulk);
              
              // FIX #13: Check again if component is still mounted before setting state
              if (abortControllerRef.current?.signal.aborted) {
                logger.debug('Bulk pricing response received but component unmounted - ignoring');
                return;
              }
              
              logger.debug('Bulk pricing response', { 
                success: bulkResponse.success,
                hasBulkPricing: !!bulkResponse.bulkPricing,
                bulkPricing: bulkResponse.bulkPricing,
                pricing: bulkResponse.pricing,
                message: bulkResponse.message
              });
              if (bulkResponse.success && bulkResponse.bulkPricing) {
                // FIX #13: Check again before setting state
                if (!abortControllerRef.current?.signal.aborted) {
                  // CRITICAL: Not restoring - this is a fresh calculation, so update prevRef
                  prevBulkPricingRef.current = bulkResponse;
                  setBulkPricing(bulkResponse);
                  // Store bulk pricing in sessionStorage for future remounts
                  if (typeof sessionStorage !== 'undefined') {
                    const bulkPricingKey = `paymentForm_bulkPricing_${paramsKey}`;
                    sessionStorage.setItem(bulkPricingKey, JSON.stringify(bulkResponse));
                  }
                  logger.info('Bulk pricing applied successfully', { 
                    discount: bulkResponse.pricing.discountAmount,
                    originalPrice: bulkResponse.pricing.originalPrice,
                    finalPrice: bulkResponse.pricing.finalPrice,
                    savings: bulkResponse.pricing.savings,
                    ruleName: bulkResponse.bulkPricing.name
                  });
                }
              } else {
                logger.warn('No bulk pricing available', { 
                  response: bulkResponse,
                  message: bulkResponse.message || 'No bulk pricing rule found'
                });
                // FIX #13: Only set state if component is still mounted
                if (!abortControllerRef.current?.signal.aborted) {
                  setBulkPricing(null); // Explicitly clear bulk pricing if not available
                }
              }
            } catch (bulkError) {
              logger.error('Error calculating bulk pricing', { 
                bulkError, 
                errorMessage: bulkError.message,
                errorStack: bulkError.stack
              });
              // FIX #13: Only set state if component is still mounted
              if (!abortControllerRef.current?.signal.aborted) {
                setBulkPricing(null); // Clear bulk pricing on error
              }
            } finally {
              // FIX #6 & #14: Clear loading state (only if component is still mounted)
              if (!abortControllerRef.current?.signal.aborted) {
                setBulkPricingLoading(false);
              }
            }
          } else {
            // FIX #35: Clear bulk pricing if only one course (no need to call API)
            setBulkPricing(null);
            setBulkPricingLoading(false);
          }
        } else if (courseId) {
          // Single course
          logger.debug('Single course', { courseId });
          const response = await api.getCoursePricing(courseId);
          logger.debug('Single course response', { response });
          setCourses([response.course]);
          // FIX: Only set sessionStorage if we actually have course data
          if (typeof sessionStorage !== 'undefined' && response.course) {
            sessionStorage.setItem(storageKey, 'true'); // Mark as fetched for these params (persists across remounts)
          }
          
          // FIX #8: Store initial course price for validation
          initialCoursePricesRef.current.clear();
          initialCoursePricesRef.current.set(response.course.id, parseFloat(response.course.price) || 0);
        }
      } catch (error) {
        logger.error('Error fetching course(s)', { error, courseIds, courseId, productIds, errorMessage: error.message, errorStack: error.stack });
        // FIX #11: Use formatErrorMessage for consistent error message formatting
        // FIX #20: Distinguish between network errors, timeout errors, and server errors
        // FIX: Prevent errors from bubbling up to ErrorBoundary by ensuring all errors are caught
        const isTimeoutError = error.isTimeout || error.message?.includes('timeout') || error.name === 'AbortError';
        const isNetworkError = !error.response && (error.message?.includes('fetch') || error.message?.includes('network'));
        const isAbortError = error.name === 'AbortError' || abortControllerRef.current?.signal.aborted;
        
        // Don't set error state if request was aborted (component unmounted)
        if (isAbortError) {
          logger.debug('Request aborted, not setting error state');
          return;
        }
        
        // FIX: Provide product-specific error messages
        const defaultErrorMessage = productIds
          ? 'Failed to load product information. Please try again.'
          : 'Failed to load course information. Please try again.';
        
        const errorMessage = isTimeoutError
          ? 'Request timed out. Please check your internet connection and try again.'
          : isNetworkError
          ? 'Network error. Please check your connection and try again.'
          : (error.message && (error.message.includes('not found') || error.message.includes('not available'))
            ? error.message 
            : defaultErrorMessage);
        
        // FIX: Ensure error state is set safely to prevent React errors
        try {
          setError(formatErrorMessage(errorMessage));
          setCourses([]); // Ensure courses array is empty so error message displays
          sessionStorage.removeItem(storageKey); // Remove on error so it can retry
        } catch (stateError) {
          // If setting state fails, log but don't throw (prevents ErrorBoundary trigger)
          logger.error('Failed to set error state', { stateError, originalError: error });
        }
      } finally {
        // FIX: Ensure loading state is always cleared, even if there's an error
        try {
          if (!abortControllerRef.current?.signal.aborted) {
            setLoading(false);
          }
          // CRITICAL: Always clear fetching flag
          isFetchingRef.current = false;
        } catch (stateError) {
          logger.error('Failed to clear loading state', { stateError });
          isFetchingRef.current = false;
        }
      }
    };

    // FIX: Only fetch if we have params and haven't already fetched
    // The alreadyFetched check above should prevent this, but add extra guard
    if (courseId || courseIds || productIds) {
      // Double-check sessionStorage before fetching (defense in depth)
      const doubleCheckFetched = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(storageKey) === 'true';
      
      if (!doubleCheckFetched && !isFetchingRef.current) {
        fetchData();
      } else {
        // Already fetched or currently fetching - ensure loading is false
        setLoading(false);
      }
    }
    
    // FIX #13: Cleanup - abort API calls if component unmounts
    return () => {
      abortController.abort();
      abortControllerRef.current = null;
      // Don't clear sessionStorage on cleanup - we want to prevent refetch on remount
      // The sessionStorage will persist across remounts (e.g., when Elements remounts due to clientSecret change)
    };
    // Intentionally excluding courses.length and error from dependencies:
    // - courses.length is only checked to clear sessionStorage if empty (one-time check)
    // - error is only checked for logging purposes
    // - Adding them would cause infinite loops since the effect sets courses/error
    // - We use sessionStorage to prevent refetch loops instead
    // CRITICAL: Also exclude bulkPricingLoading to prevent loops when bulk pricing is calculated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, courseIds, productIds]); // Only depend on URL params, not courses state or bulk pricing

  // FIX #9: Memoize total price calculation to prevent unnecessary recalculations
  // FIX #23: Add price precision handling to prevent floating point errors
  const getTotalPrice = useCallback(() => {
    // Calculate base total from courses/products
    // Use isPaidCourse check to match the subtotal display logic
    // FIX #22: Clear separation - handle courses and products the same way (products are converted to course-like format)
    // FIX: Add comprehensive null safety check for courses array
    // Handle case where courses might be an object with a courses property
    let coursesArray = null;
    
    // First check if courses is already an array
    if (Array.isArray(courses)) {
      coursesArray = courses;
    } 
    // If it's an object, try to extract the array from common property names
    else if (courses && typeof courses === 'object') {
      // Try common property names where the array might be stored
      if (courses.courses && Array.isArray(courses.courses)) {
        coursesArray = courses.courses;
        logger.debug('getTotalPrice: extracted courses array from courses.courses property', { 
          originalKeys: Object.keys(courses),
          arrayLength: coursesArray.length
        });
      } else if (courses.data && Array.isArray(courses.data)) {
        coursesArray = courses.data;
        logger.debug('getTotalPrice: extracted courses array from courses.data property');
      } else if (courses.items && Array.isArray(courses.items)) {
        coursesArray = courses.items;
        logger.debug('getTotalPrice: extracted courses array from courses.items property');
      } else {
        // Log detailed info for debugging
        logger.warn('getTotalPrice called with invalid courses (object without array property)', { 
          courses, 
          type: typeof courses,
          isArray: Array.isArray(courses),
          keys: courses ? Object.keys(courses) : null,
          hasCoursesProp: courses ? 'courses' in courses : false,
          coursesPropType: courses && courses.courses ? typeof courses.courses : null
        });
        return 0;
      }
    }
    // If courses is null/undefined, silently return 0 (normal during initial load)
    else if (courses === null || courses === undefined) {
      return 0;
    }
    // Any other type is invalid
    else {
      logger.warn('getTotalPrice called with invalid courses type', { 
        courses, 
        type: typeof courses
      });
      return 0;
    }
    
    // Final validation of the extracted array
    if (!coursesArray || !Array.isArray(coursesArray)) {
      // Only warn if we actually had a non-empty value (not just empty during initial load)
      const hasNonEmptyValue = courses !== null && 
                               courses !== undefined && 
                               !(Array.isArray(courses) && courses.length === 0);
      if (hasNonEmptyValue) {
        logger.warn('getTotalPrice: extracted array is invalid', { 
          coursesArray, 
          originalCourses: courses,
          type: typeof coursesArray,
          isArray: Array.isArray(coursesArray),
          length: coursesArray ? coursesArray.length : null
        });
      }
      return 0;
    }
    
    // Empty array is valid (just means no courses, total is 0)
    if (coursesArray.length === 0) {
      return 0;
    }
    
    const baseTotal = coursesArray.reduce((sum, course) => {
      // FIX: Add null check for course object
      if (!course) {
        logger.warn('Invalid course object in courses array', { course });
        return sum;
      }
      // Handle both courses and products (products are already converted to course-like format in fetchData)
      // Check if course is paid before including price
      const price = (course.isPaidCourse || course.is_paid_course) ? (parseFloat(course.price) || 0) : 0;
      // FIX #23: Use Math.round to prevent floating point precision errors
      // Multiply by 100, add, then divide by 100 to maintain 2 decimal precision
      return Math.round((sum + price) * 100) / 100;
    }, 0);

    logger.debug('getTotalPrice calculation', { 
      baseTotal, 
      coursesCount: coursesArray.length,
      courses: (coursesArray && Array.isArray(coursesArray)) ? coursesArray.filter(c => c && c.id).map(c => ({ id: c.id, name: c.name, price: c.price, isPaidCourse: c.isPaidCourse || c.is_paid_course })) : [],
      hasBulkPricing: !!bulkPricing,
      hasAppliedPromo: !!appliedPromo,
      bulkPricingData: bulkPricing ? {
        finalPrice: bulkPricing.pricing?.finalPrice,
        discountAmount: bulkPricing.pricing?.discountAmount,
        originalPrice: bulkPricing.pricing?.originalPrice
      } : null
    });

    // Apply EITHER bulk pricing OR promo code discount (mutually exclusive)
    // Priority: Promo code takes precedence over bulk pricing
    let finalTotal = baseTotal;
    
    if (appliedPromo) {
      // Apply promo code discount (promo code takes priority)
      // FIX #4: Add precision handling to prevent floating point errors (consistent with bulk pricing)
      if (appliedPromo.discount_type === 'percentage') {
        const discountValue = parseFloat(appliedPromo.discount_value) || 0;
        const discountAmount = baseTotal * (discountValue / 100);
        // FIX #4: Use Math.round to prevent floating point precision errors
        finalTotal = Math.max(0, Math.round((baseTotal - discountAmount) * 100) / 100);
        logger.debug('Applying percentage promo code discount', { 
          baseTotal, 
          discountValue,
          discountAmount,
          finalTotal, 
          discountType: appliedPromo.discount_type,
          promoCode: appliedPromo.code
        });
      } else {
        const discountValue = parseFloat(appliedPromo.discount_value) || 0;
        // FIX #4: Use Math.round to prevent floating point precision errors
        finalTotal = Math.max(0, Math.round((baseTotal - discountValue) * 100) / 100);
        logger.debug('Applying fixed amount promo code discount', { 
          baseTotal, 
          discountValue,
          finalTotal, 
          discountType: appliedPromo.discount_type,
          promoCode: appliedPromo.code
        });
      }
    } else if (bulkPricing && bulkPricing?.pricing?.finalPrice && !productIds) {
      // FIX #6: Apply bulk pricing discount only if no promo code is applied (with null check)
      // Use optional chaining for consistency and safety
      finalTotal = bulkPricing?.pricing?.finalPrice || baseTotal;
      logger.debug('Applying bulk pricing discount', { 
        baseTotal, 
        finalTotal, 
        discountAmount: bulkPricing?.pricing?.discountAmount,
        originalPrice: bulkPricing?.pricing?.originalPrice,
        savings: bulkPricing?.pricing?.savings
      });
    } else {
      logger.debug('No discount applied', { baseTotal, finalTotal, hasBulkPricing: !!bulkPricing, hasAppliedPromo: !!appliedPromo });
    }

    return finalTotal;
  }, [courses, bulkPricing, appliedPromo, productIds]);
  
  // FIX #9: Memoize total price for display to prevent recalculations on every render
  // getTotalPrice already depends on courses, so we only need getTotalPrice in the dependency array
  const totalPrice = useMemo(() => {
    const price = getTotalPrice();
    console.log('[FREE_ENROLLMENT] totalPrice memoized value:', {
      price,
      isZero: price === 0
    });
    return price;
  }, [getTotalPrice]);

  const createPaymentIntent = useCallback(async (forceRecreate = false) => {
    // FIX: Use ref to check current value (avoids dependency on paymentIntentCreated)
    if (paymentIntentCreatedRef.current && !forceRecreate) {
      logger.debug('Payment intent already created, skipping');
      return;
    }
    
    // CRITICAL FIX #1: Prevent concurrent payment intent creation using mutex
    if (paymentIntentCreationLockRef.current) {
      logger.debug('Payment intent creation locked (already in progress), skipping');
      return;
    }
    
    // Prevent concurrent payment intent creation
    if (processing) {
      logger.debug('Payment intent creation already in progress, skipping');
      return;
    }
    
    // FIX #31: Rate limiting - prevent rapid API calls
    const now = Date.now();
    const lastCallTime = apiCallTimestampsRef.current.createPaymentIntent;
    if (lastCallTime && (now - lastCallTime) < RATE_LIMIT_DELAY) {
      logger.debug('Payment intent creation rate limited, skipping');
      return;
    }
    apiCallTimestampsRef.current.createPaymentIntent = now;
    
    // Prevent rapid retries (debounce) - wait at least 2 seconds between attempts
    if (lastPaymentIntentAttemptRef.current && (now - lastPaymentIntentAttemptRef.current) < 2000) {
      logger.debug('Payment intent creation debounced, skipping');
      return;
    }
    lastPaymentIntentAttemptRef.current = now;
    
    // FIX #39: Simplified error blocking logic - only block if error exists and not forcing recreation
    // User can retry by applying/removing promo code or refreshing
    // Removed redundant check - single condition is sufficient
    if (error && !forceRecreate) {
      logger.debug('Payment intent creation blocked due to existing error (user can retry):', error);
      return;
    }
    
    // FIX: Use ref to get current productIds value FIRST to avoid stale closures
    // This must be done early so we can use it consistently throughout the function
    const currentProductIds = productIdsRef.current || productIds;
    
    // FIX: For products, ensure we have valid courses data before creating payment intent
    // If productIds exists but courses is empty, don't create payment intent (product fetch likely failed)
    // CRITICAL: Also check error state and loading state to prevent race conditions
    if (currentProductIds) {
      const currentCourses = coursesRef.current || courses;
      const currentError = error; // Check current error state
      const currentLoading = loading; // Check current loading state
      
      // Block if still loading AND we don't have courses data in ref or state
      // If we have courses in the ref, we can proceed even if loading is true (data restored from sessionStorage)
      const hasCoursesData = (currentCourses && currentCourses.length > 0) || (coursesRef.current && coursesRef.current.length > 0);
      if (currentLoading && !hasCoursesData) {
        logger.debug('Payment intent creation blocked: products are still loading', {
          productIds: currentProductIds
        });
        return;
      }
      
      // Block if there's an error (product fetch failed)
      if (currentError) {
        logger.debug('Payment intent creation blocked: error exists (product fetch likely failed)', {
          productIds: currentProductIds,
          error: currentError
        });
        return;
      }
      
      // Block if courses array is empty (product fetch failed or not completed)
      if (!currentCourses || !Array.isArray(currentCourses) || currentCourses.length === 0) {
        logger.debug('Payment intent creation blocked: products expected but courses array is empty', {
          productIds: currentProductIds,
          coursesLength: currentCourses?.length || 0,
          loading: currentLoading,
          error: currentError
        });
        return;
      }
    }
    
    // Acquire lock
    paymentIntentCreationLockRef.current = true;
    
    // FIX #5: Use single source of truth - getTotalPrice() function
    // FIX: Use refs to get current state values to avoid stale closures
    const currentAppliedPromo = appliedPromoRef.current || appliedPromo;
    const currentBulkPricing = bulkPricingRef.current || bulkPricing;
    
    // Calculate total with current state values (not stale closure values)
    // FIX: Use refs to get current values to avoid dependency on courses array reference
    // FIX: Use currentProductIds instead of productIds from closure
    const totalAmount = (() => {
      const currentCourses = coursesRef.current || courses;
      const baseTotal = currentCourses.reduce((sum, course) => {
        const price = (course.isPaidCourse || course.is_paid_course) ? (parseFloat(course.price) || 0) : 0;
        return Math.round((sum + price) * 100) / 100;
      }, 0);
      
      console.log('[FREE_ENROLLMENT] Calculating totalAmount in createPaymentIntent', {
        baseTotal,
        hasPromo: !!currentAppliedPromo,
        promoCode: currentAppliedPromo?.code,
        promoType: currentAppliedPromo?.discount_type,
        promoValue: currentAppliedPromo?.discount_value,
        hasBulkPricing: !!currentBulkPricing,
        courseCount: currentCourses.length
      });
      
      if (currentAppliedPromo) {
        if (currentAppliedPromo.discount_type === 'percentage') {
          const discountValue = parseFloat(currentAppliedPromo.discount_value) || 0;
          const discountAmount = baseTotal * (discountValue / 100);
          const finalAmount = Math.max(0, Math.round((baseTotal - discountAmount) * 100) / 100);
          console.log('[FREE_ENROLLMENT] Applied percentage discount', {
            baseTotal,
            discountValue,
            discountAmount,
            finalAmount,
            isZero: finalAmount === 0
          });
          return finalAmount;
        } else {
          const discountValue = parseFloat(currentAppliedPromo.discount_value) || 0;
          const finalAmount = Math.max(0, Math.round((baseTotal - discountValue) * 100) / 100);
          console.log('[FREE_ENROLLMENT] Applied fixed discount', {
            baseTotal,
            discountValue,
            finalAmount,
            isZero: finalAmount === 0
          });
          return finalAmount;
        }
      } else if (currentBulkPricing && currentBulkPricing?.pricing?.finalPrice && !currentProductIds) {
        // FIX: Use currentProductIds instead of productIds from closure
        const finalAmount = Math.round(parseFloat(currentBulkPricing.pricing.finalPrice) * 100) / 100;
        console.log('[FREE_ENROLLMENT] Using bulk pricing', { finalAmount, isZero: finalAmount === 0 });
        return finalAmount;
      }
      console.log('[FREE_ENROLLMENT] No discount applied, using baseTotal', { baseTotal, isZero: baseTotal === 0 });
      return baseTotal;
    })();
    
    console.log('[FREE_ENROLLMENT] Final totalAmount calculated:', {
      totalAmount,
      isZero: totalAmount === 0,
      isNaN: isNaN(totalAmount),
      isLessThanZero: totalAmount < 0
    });
    
    // CRITICAL FIX #2: Validate payment amount before creating payment intent
    // Allow $0 for free enrollments (100% promo code discount)
    if (totalAmount === undefined || totalAmount === null || isNaN(totalAmount) || totalAmount < 0) {
      console.error('[FREE_ENROLLMENT] ❌ Invalid totalAmount - rejecting', {
        totalAmount,
        type: typeof totalAmount,
        isNaN: isNaN(totalAmount),
        isLessThanZero: totalAmount < 0
      });
      const currentCourses = coursesRef.current || courses;
      logger.error('Invalid payment amount calculated', { 
        totalAmount, 
        courses: currentCourses.map(c => ({ id: c.id, name: c.name, price: c.price })),
        appliedPromo: currentAppliedPromo,
        bulkPricing: currentBulkPricing ? currentBulkPricing.pricing : null,
        productIds: currentProductIds
      });
      paymentIntentCreationLockRef.current = false; // Release lock
      setError('Invalid payment amount. Please ensure courses have valid prices.');
      setProcessing(false);
      return;
    }
    // Note: totalAmount === 0 is allowed for free enrollments (handled by backend)
    
    if (totalAmount === 0) {
      console.log('[FREE_ENROLLMENT] ✅ totalAmount is $0 - backend will skip Stripe and create enrollments directly');
      logger.info('Free enrollment detected - backend will skip Stripe payment intent creation');
    }
    
    // FIX: Get current courses for API call (currentAppliedPromo and currentBulkPricing already declared above)
    const currentCourses = coursesRef.current || courses;
    
    console.log('[FREE_ENROLLMENT] About to call backend API:', {
      forceRecreate,
      totalAmount,
      isZero: totalAmount === 0,
      courseIds: currentCourses.map(c => c.id),
      courseCount: currentCourses.length,
      hasPromo: !!currentAppliedPromo,
      promoCode: currentAppliedPromo?.code,
      note: totalAmount === 0 
        ? 'Backend endpoint will detect $0 and skip Stripe, creating enrollments directly' 
        : 'Backend endpoint will create Stripe payment intent'
    });
    
    logger.debug('Creating payment intent', {
      forceRecreate,
      totalAmount,
      appliedPromo: currentAppliedPromo,
      bulkPricing: currentBulkPricing ? currentBulkPricing.pricing : null,
      productIds: currentProductIds,
      productIdsFromParams: productIds,
      productIdsFromRef: productIdsRef.current
    });
    
    setProcessing(true);
    setError(null);
    
    // CRITICAL FIX #4: Don't clear client secret when recreating - this causes remount loops
    // Instead, we'll update the payment intent with the new amount
    // The Stripe Elements component can handle payment intent updates without remounting
    // Only clear if we're forcing a complete reset (which shouldn't happen during normal discount changes)
    // if (forceRecreate && !processing && clientSecret) {
    //   setClientSecret(null);
    //   onClientSecretChange?.(null);
    // }

    try {
      let response;
      
      // FIX: Use ref value to ensure we have the latest productIds
      // CRITICAL: Check both currentProductIds AND productIds from params to catch any edge cases
      const hasProductIds = !!(currentProductIds || productIds);
      
      if (hasProductIds) {
        // FIX: Use the most reliable source - prefer currentProductIds from ref, fallback to productIds from params
        const productIdsToUse = currentProductIds || productIds;
        // FIX: Use current state values from refs to avoid stale closures
        // FIX: Use productIdsToUse to ensure we have the latest value
        const productIdsArray = productIdsToUse.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
        
        // CRITICAL: Double-check we're not accidentally treating products as courses
        if (productIdsArray.length === 0) {
          logger.error('⚠️ Product IDs array is empty after parsing', {
            productIdsToUse,
            currentProductIds,
            productIdsFromParams: productIds
          });
          throw new Error('Invalid product IDs');
        }
        
        logger.info('🛒 Creating PRODUCT payment intent', { 
          productIds: productIdsArray, 
          totalAmount,
          hasPromoCode: !!currentAppliedPromo,
          productIdsSource: currentProductIds ? 'ref' : 'params'
        });
        response = await api.createProductPaymentIntent({
          productIds: productIdsArray,
          totalAmount,
          promoCode: currentAppliedPromo
        });
      } else {
        // FIX: This is the COURSE route - ensure we're not accidentally routing products here
        // Log warning if we detect products in courses data but no productIds
        const hasProductTypeInCourses = currentCourses.some(c => c.type === 'product');
        if (hasProductTypeInCourses) {
          logger.error('⚠️ CRITICAL: Products detected in courses array but no productIds in params/ref - routing to COURSE endpoint incorrectly!', {
            courses: currentCourses.map(c => ({ id: c.id, name: c.name, type: c.type })),
            productIdsFromParams: productIds,
            productIdsFromRef: productIdsRef.current,
            currentProductIds
          });
        }
        
        const paymentRequest = {
          totalAmount,
          // Only send bulk pricing if no promo code is applied (mutually exclusive)
          // FIX: Use current state values from refs to avoid stale closures
          bulkPricing: currentAppliedPromo ? null : currentBulkPricing,
          promoCode: currentAppliedPromo,
          courseIds: currentCourses.map(course => parseInt(course.id)) // Ensure course IDs are integers
        };
        
        // Debug: Log what we're sending to the backend
        logger.info('💳 Creating COURSE payment intent with:', {
          totalAmount,
          hasPromoCode: !!currentAppliedPromo,
          promoCode: currentAppliedPromo ? {
            code: currentAppliedPromo.code,
            discount_type: currentAppliedPromo.discount_type,
            discount_value: currentAppliedPromo.discount_value
          } : null,
          hasBulkPricing: !!currentBulkPricing,
          courseIds: paymentRequest.courseIds,
          courseCount: currentCourses.length
        });
        
        logger.debug('Payment request data', { 
          totalAmount, 
          hasPromoCode: !!appliedPromo,
          promoCode: appliedPromo ? {
            code: appliedPromo.code,
            discount_type: appliedPromo.discount_type,
            discount_value: appliedPromo.discount_value
          } : null,
          bulkPricing: appliedPromo ? null : (bulkPricing ? {
            discountAmount: bulkPricing.pricing?.discountAmount,
            finalPrice: bulkPricing.pricing?.finalPrice
          } : null),
          courseIds: paymentRequest.courseIds,
          baseTotal: (courses && Array.isArray(courses) && courses.length > 0) ? courses.reduce((sum, course) => {
            if (!course) return sum;
            const price = (course.isPaidCourse || course.is_paid_course) ? (parseFloat(course.price) || 0) : 0;
            return Math.round((sum + price) * 100) / 100;
          }, 0) : 0
        });
        console.log('[FREE_ENROLLMENT] 📤 Making API call to createPaymentIntent with:', {
          totalAmount: paymentRequest.totalAmount,
          isZero: paymentRequest.totalAmount === 0,
          courseIds: paymentRequest.courseIds,
          hasPromoCode: !!paymentRequest.promoCode,
          promoCode: paymentRequest.promoCode?.code,
          hasBulkPricing: !!paymentRequest.bulkPricing
        });
        
        response = await api.createPaymentIntent(paymentRequest);
        
        console.log('[FREE_ENROLLMENT] 📥 API Response received:', {
          success: response?.success,
          freeEnrollment: response?.freeEnrollment,
          redirectTo: response?.redirectTo,
          amount: response?.amount,
          paymentIntentId: response?.paymentIntentId,
          hasClientSecret: !!response?.clientSecret,
          hasCourses: !!response?.courses,
          courseCount: response?.courses?.length,
          hasUser: !!response?.user,
          message: response?.message,
          responseType: typeof response,
          responseKeys: response ? Object.keys(response) : []
        });
      }

      // FIX #31: Never log clientSecret - only log payment intent ID (security fix)
      // Removed any potential clientSecret logging
      console.log('[FREE_ENROLLMENT] ✅ API Response received:', {
        success: response.success,
        freeEnrollment: response.freeEnrollment,
        redirectTo: response.redirectTo,
        amount: response.amount,
        paymentIntentId: response.paymentIntentId,
        hasClientSecret: !!response.clientSecret,
        hasCourses: !!response.courses,
        courseCount: response.courses?.length,
        hasUser: !!response.user,
        message: response.message,
        totalAmount,
        responseKeys: Object.keys(response || {})
      });
      
      logger.debug('Payment intent created', {
        paymentIntentId: response.paymentIntentId || 'unknown',
        totalAmount,
        hasClientSecret: !!response.clientSecret, // Only log existence, not value
        redirectTo: response.redirectTo,
        appliedPromo: appliedPromo ? { code: appliedPromo.code, discount_type: appliedPromo.discount_type } : null,
        bulkPricing: bulkPricing ? { discountAmount: bulkPricing.pricing?.discountAmount } : null
      });

      // Check if this is a free enrollment ($0 total) - backend returns freeEnrollment: true or redirectTo: 'confirmation'
      const isFreeEnrollmentResponse = response.freeEnrollment === true || response.redirectTo === 'confirmation';
      const isZeroAmount = totalAmount === 0 || response.amount === 0;
      const shouldHandleAsFree = isFreeEnrollmentResponse && isZeroAmount;
      
      console.log('[FREE_ENROLLMENT] Checking if response is free enrollment:', {
        isFreeEnrollmentResponse,
        isZeroAmount,
        shouldHandleAsFree,
        freeEnrollment: response.freeEnrollment,
        redirectTo: response.redirectTo,
        totalAmount,
        responseAmount: response.amount
      });
      
      if (shouldHandleAsFree) {
        console.log('[FREE_ENROLLMENT] ✅ Free enrollment detected - navigating to confirmation', {
          freeEnrollment: response.freeEnrollment,
          redirectTo: response.redirectTo,
          totalAmount,
          responseAmount: response.amount,
          courses: response.courses,
          user: response.user
        });
        
        logger.info('Free enrollment completed - redirecting to confirmation', {
          freeEnrollment: response.freeEnrollment,
          redirectTo: response.redirectTo,
          totalAmount,
          responseAmount: response.amount
        });
        // Navigate to confirmation page with enrollment data
        // Structure data to match what PurchaseConfirmation expects
        const purchaseData = {
          success: true,
          message: response.message || 'Enrollment successful (free course)',
          courses: response.courses || [],
          user: response.user || {},
          enrolledCourseIds: response.enrolledCourseIds || [],
          payment: {
            amount: 0,
            currency: response.currency || 'usd',
            receiptUrl: null, // No receipt for free enrollments
            paymentIntentId: response.paymentIntentId || null
          },
          promoCode: response.promoCode || null, // Include promo code if available
          redirectTo: 'confirmation'
        };
        
        console.log('[FREE_ENROLLMENT] 🚀 Navigating to confirmation page with data:', {
          purchaseData,
          courseCount: purchaseData.courses.length,
          hasUser: !!purchaseData.user,
          hasPayment: !!purchaseData.payment
        });
        
        navigate('/confirmation', {
          state: { purchaseData }
        });
        setProcessing(false);
        paymentIntentCreatedRef.current = false; // Reset ref since we're not creating a payment intent
        paymentIntentCreationLockRef.current = false; // Release lock on success
        console.log('[FREE_ENROLLMENT] ✅ Navigation initiated, returning early');
        return; // Don't set up payment form for free enrollments
      } else {
        console.log('[FREE_ENROLLMENT] ⚠️ Response is NOT a free enrollment - continuing with normal payment flow', {
          isFreeEnrollmentResponse,
          isZeroAmount,
          hasClientSecret: !!response.clientSecret
        });
      }

      // CRITICAL: Set refs BEFORE updating clientSecret to prevent race conditions
      // This ensures that if the component remounts due to clientSecret change,
      // the refs are already set and prevent duplicate payment intent creation
      setPaymentIntentCreated(true);
      paymentIntentCreatedRef.current = true; // Update ref immediately BEFORE clientSecret change
      initialPaymentIntentAttemptedRef.current = true; // Mark as attempted
      
      // FIX: Only update clientSecret if it actually changed to prevent unnecessary remounts
      // CRITICAL: When forceRecreate is true, we MUST update the clientSecret and notify parent
      // so that Stripe Elements uses the new payment intent (with the correct discounted amount)
      if (response.clientSecret !== clientSecret) {
        setClientSecret(response.clientSecret);
        // Always notify parent when clientSecret changes, even on forceRecreate
        // This ensures Stripe Elements uses the new payment intent with the correct amount
        // The parent will handle remounting Elements if needed
        onClientSecretChange?.(response.clientSecret);
        if (forceRecreate) {
          logger.info('🔄 Payment intent recreated with new amount', {
            oldAmount: clientSecret ? 'existing' : 'none',
            newAmount: totalAmount,
            hasPromoCode: !!currentAppliedPromo,
            promoCode: currentAppliedPromo?.code
          });
        }
      }
      setProcessing(false);
      paymentIntentCreationLockRef.current = false; // Release lock on success
    } catch (error) {
      console.error('[FREE_ENROLLMENT] ❌ Error creating payment intent:', {
        errorMessage: error.message,
        errorStatus: error.status || error.response?.status,
        errorName: error.name,
        totalAmount,
        errorData: error.data || error.response?.data,
        errorKeys: Object.keys(error || {}),
        hasErrorData: !!(error.data || error.response?.data)
      });
      
      logger.error('Error creating payment intent', { 
        error, 
        status: error.status || error.response?.status,
        errorData: error.data || error.response?.data,
        errorDetails: (error.data || error.response?.data)?.details,
        errorType: (error.data || error.response?.data)?.type,
        errorCode: (error.data || error.response?.data)?.code,
        totalAmount
      });
      
      // Check if this might be a free enrollment that failed - check error response for free enrollment indicators
      // The API service attaches error data as error.data (not error.response.data)
      const errorData = error.data || error.response?.data;
      
      console.log('[FREE_ENROLLMENT] Checking error response for free enrollment indicators:', {
        totalAmount,
        isZero: totalAmount === 0,
        hasErrorData: !!errorData,
        errorDataKeys: errorData ? Object.keys(errorData) : [],
        freeEnrollment: errorData?.freeEnrollment,
        redirectTo: errorData?.redirectTo,
        hasCourses: !!errorData?.courses,
        hasUser: !!errorData?.user
      });
      
      if (totalAmount === 0 && errorData && (errorData.freeEnrollment === true || errorData.redirectTo === 'confirmation')) {
        console.log('[FREE_ENROLLMENT] ✅ Free enrollment detected in ERROR response - attempting to handle', {
          errorData,
          totalAmount,
          errorStatus: error.status || error.response?.status,
          hasCourses: !!errorData.courses,
          hasUser: !!errorData.user
        });
        
        logger.info('Free enrollment detected in error response - attempting to handle', {
          errorData,
          totalAmount,
          errorStatus: error.status || error.response?.status
        });
        // Try to navigate with the error data if it contains enrollment info
        if (errorData.courses && errorData.user) {
          console.log('[FREE_ENROLLMENT] 🚀 Error response has courses and user - navigating to confirmation', {
            courseCount: errorData.courses.length,
            userEmail: errorData.user.email,
            enrolledCourseIds: errorData.enrolledCourseIds
          });
          
          const purchaseData = {
            success: true,
            message: errorData.message || 'Enrollment successful (free course)',
            courses: errorData.courses || [],
            user: errorData.user || {},
            enrolledCourseIds: errorData.enrolledCourseIds || [],
            payment: {
              amount: 0,
              currency: errorData.currency || 'usd',
              receiptUrl: null,
              paymentIntentId: errorData.paymentIntentId || null
            },
            promoCode: errorData.promoCode || null,
            redirectTo: 'confirmation'
          };
          
          navigate('/confirmation', {
            state: { purchaseData }
          });
          setProcessing(false);
          paymentIntentCreatedRef.current = false;
          paymentIntentCreationLockRef.current = false;
          console.log('[FREE_ENROLLMENT] ✅ Navigation from error response completed');
          return;
        } else {
          console.warn('[FREE_ENROLLMENT] ⚠️ Error response indicates free enrollment but missing data:', {
            hasCourses: !!errorData.courses,
            hasUser: !!errorData.user,
            errorDataKeys: Object.keys(errorData || {})
          });
        }
      } else {
        console.log('[FREE_ENROLLMENT] ❌ Error is NOT a free enrollment scenario:', {
          totalAmount,
          isZero: totalAmount === 0,
          hasErrorData: !!errorData,
          freeEnrollment: errorData?.freeEnrollment,
          redirectTo: errorData?.redirectTo
        });
      }
      
      // FIX #11: Use formatErrorMessage for consistent error message formatting
      // FIX #20: Distinguish between network errors, timeout errors, and server errors
      // FIX #28: Add retry mechanism for transient errors
      const isTimeoutError = error.isTimeout || error.message?.includes('timeout') || error.name === 'AbortError';
      const isNetworkError = !error.status && !error.response && (error.message?.includes('fetch') || error.message?.includes('network'));
      const errorStatus = error.status || error.response?.status;
      const isRetryable = isTimeoutError || isNetworkError || (errorStatus >= 500 && errorStatus < 600);
      
      // FIX: Handle 409 Conflict errors specifically (already enrolled, course full, etc.)
      let errorMessage;
      if (errorStatus === 409) {
        // 409 Conflict - user is already enrolled or course is full
        errorMessage = errorData?.message || errorData?.error || 'You are already enrolled in this course or the course is full.';
        logger.warn('409 Conflict - enrollment issue', { 
          message: errorMessage,
          errorData
        });
      } else if (errorStatus === 500) {
        // 500 Internal Server Error - show backend error details if available
        const backendMessage = errorData?.message || errorData?.details || errorData?.error;
        errorMessage = backendMessage || 'Server error occurred while creating payment intent. Please try again.';
      } else {
        errorMessage = isTimeoutError
          ? 'Request timed out. Please check your internet connection and try again.'
          : isNetworkError
          ? 'Network error. Please check your connection and try again.'
          : (errorData?.message || error.message || 'Failed to create payment intent');
      }
      
      setError(formatErrorMessage(errorMessage + (isRetryable ? ' You can try again by applying or removing a promo code.' : '')));
      setProcessing(false);
      // Reset the last attempt time on error to allow retry after delay
      lastPaymentIntentAttemptRef.current = null;
      paymentIntentCreationLockRef.current = false; // Release lock on error
    }
  // FIX #36: Optimize dependencies - use refs for values that don't need to trigger recreation
  // Note: getTotalPrice is not needed as a dependency since createPaymentIntent calculates total directly using refs
  // CRITICAL FIX: Remove paymentIntentCreated from dependencies to prevent infinite loop
  // paymentIntentCreated is only used in a guard clause via paymentIntentCreatedRef, not directly
  // CRITICAL FIX: Remove courses and bulkPricing from dependencies - they cause recreation on every state update
  // Use refs to access current values instead (coursesRef, bulkPricingRef)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIds, appliedPromo, onClientSecretChange, processing, error]);

  // Sync clientSecret from parent (set after parent mounts Elements with options)
  // FIX: Only update if the value actually changed to prevent unnecessary re-renders
  useEffect(() => {
    if (clientSecretFromParent && clientSecretFromParent !== clientSecret) {
      setClientSecret(clientSecretFromParent);
      setPaymentIntentCreated(true);
      paymentIntentCreatedRef.current = true; // Update ref immediately
      initialPaymentIntentAttemptedRef.current = true; // Mark as attempted since parent provided it
    }
    // Only react when value changes from parent
  }, [clientSecretFromParent, clientSecret]);
  
  // FIX #26: Clear success state on unmount or when courses change
  // FIX #29: Handle browser back button navigation
  useEffect(() => {
    const handlePopState = () => {
      // Clear success state when user navigates back
      setSuccess(false);
      logger.debug('Browser back button detected, clearing success state');
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      // Clear success state when component unmounts
      setSuccess(false);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);
  
  // FIX #26: Also clear success when navigating away (courses change)
  useEffect(() => {
    setSuccess(false);
  }, [courseId, courseIds, productIds]);

  // Handle parent-provided clientSecret (for special flows)
  useEffect(() => {
    if (clientSecretFromParent && !clientSecret) {
      logger.info('Using clientSecret from parent', { hasClientSecretFromParent: !!clientSecretFromParent });
      setClientSecret(clientSecretFromParent);
      setPaymentIntentCreated(true);
      paymentIntentCreatedRef.current = true;
      setShowPaymentForm(true); // Show payment form if parent provided clientSecret
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSecretFromParent]);
  
  // Show payment form when clientSecret becomes available (after "Proceed to Payment" click)
  useEffect(() => {
    if (clientSecret && paymentIntentCreatedRef.current && !showPaymentForm) {
      logger.info('ClientSecret available, showing payment form');
      setShowPaymentForm(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSecret]);
  
  // Handler for "Proceed to Payment" button
  const handleProceedToPayment = async () => {
    console.log('[FREE_ENROLLMENT] handleProceedToPayment called', {
      creatingPaymentIntent,
      paymentIntentCreated: paymentIntentCreatedRef.current,
      showPaymentForm
    });
    
    if (creatingPaymentIntent || paymentIntentCreatedRef.current) {
      console.log('[FREE_ENROLLMENT] Already creating or created, returning early');
      return; // Already creating or created
    }
    
    // Check if total is $0 (free enrollment) - backend will handle it in createPaymentIntent
    // We still call createPaymentIntent, but the backend will skip Stripe and return enrollment data
    const currentTotal = getTotalPrice();
    console.log('[FREE_ENROLLMENT] Total calculated:', {
      currentTotal,
      isZero: currentTotal === 0,
      isLessThanOrEqualZero: currentTotal <= 0,
      appliedPromo: appliedPromo?.code,
      promoDiscount: appliedPromo ? `${appliedPromo.discount_type === 'percentage' ? appliedPromo.discount_value + '%' : '$' + appliedPromo.discount_value}` : null
    });
    
    // For $0 totals, we still need to call the backend to create enrollments
    // The backend will detect $0 and skip Stripe payment intent creation
    // This is intentional - we need the backend to handle enrollment creation, transaction records, and emails
    if (currentTotal === 0 || currentTotal <= 0) {
      console.log('[FREE_ENROLLMENT] ✅ Total is $0 - calling backend for free enrollment (no Stripe payment intent will be created)');
      logger.info('Total is $0 - calling backend for free enrollment');
    }
    
    setCreatingPaymentIntent(true);
    setError(null);
    
    try {
      console.log('[FREE_ENROLLMENT] Calling backend API (will skip Stripe if $0):', {
        currentTotal,
        isZero: currentTotal === 0,
        courses: courses?.map(c => ({ id: c.id, name: c.name, price: c.price })),
        courseCount: courses?.length,
        note: currentTotal === 0 ? 'Backend will detect $0 and skip Stripe, creating enrollments directly' : 'Backend will create Stripe payment intent'
      });
      logger.info('User clicked button - calling backend API', { 
        totalAmount: currentTotal,
        isFreeEnrollment: currentTotal === 0 
      });
      await createPaymentIntent(true); // Backend handles both paid and free enrollments
      
      // For free enrollments, createPaymentIntent will navigate away, so this code won't run
      // For paid enrollments, check if payment intent was created
      // Wait a moment for state to update
      console.log('[FREE_ENROLLMENT] Waiting for state update after createPaymentIntent call...');
      await new Promise(resolve => setTimeout(resolve, 300));
      
      console.log('[FREE_ENROLLMENT] After createPaymentIntent - checking state:', {
        paymentIntentCreated: paymentIntentCreatedRef.current,
        hasClientSecret: !!clientSecret,
        currentTotal,
        showPaymentForm
      });
      
      // Check if this was a free enrollment (navigation would have happened)
      // If paymentIntentCreatedRef is still false and we have no clientSecret, it might be a free enrollment
      // or an error - check if we're still on this page
      if (!paymentIntentCreatedRef.current && !clientSecret && currentTotal === 0) {
        // This might be a free enrollment that didn't navigate - check response
        console.warn('[FREE_ENROLLMENT] ⚠️ Free enrollment may not have completed - checking state', {
          paymentIntentCreated: paymentIntentCreatedRef.current,
          hasClientSecret: !!clientSecret,
          currentTotal
        });
        logger.warn('Free enrollment may not have completed - checking state');
        // The navigation should have happened in createPaymentIntent, but if it didn't, 
        // we'll let the user know there was an issue
        setError('Enrollment may have completed. Please check your courses or try again.');
      } else if (paymentIntentCreatedRef.current) {
        // Payment intent was created successfully - show the form
        console.log('[FREE_ENROLLMENT] Payment intent created successfully - showing payment form');
        setShowPaymentForm(true);
        logger.info('Payment intent created, showing payment form');
      } else if (currentTotal > 0) {
        // For paid enrollments, if payment intent wasn't created, show error
        console.error('[FREE_ENROLLMENT] ❌ Payment intent not created for paid enrollment');
        setError('Failed to initialize payment. Please try again.');
        logger.error('Payment intent not created after Proceed to Payment click');
      }
    } catch (error) {
      console.error('[FREE_ENROLLMENT] ❌ Error in handleProceedToPayment catch block:', {
        errorMessage: error.message,
        currentTotal,
        error
      });
      logger.error('Error creating payment intent on Proceed to Payment', { error });
      // Only show error if it's not a navigation (which would unmount the component)
      if (currentTotal > 0) {
        setError('Failed to initialize payment. Please try again.');
      }
    } finally {
      console.log('[FREE_ENROLLMENT] handleProceedToPayment finally block - resetting creatingPaymentIntent');
      setCreatingPaymentIntent(false);
    }
  };

  // Recreate payment intent when ANY discount changes (promo codes, bulk pricing, etc.)
  useEffect(() => {
    // CRITICAL: Skip if we're restoring bulk pricing to prevent loops
    if (isRestoringBulkPricingRef.current) {
      logger.debug('Skipping discount change check - restoring bulk pricing');
      return;
    }
    
    // FIX: Use ref to check current value to avoid dependency issues
    // Don't run if payment intent hasn't been created yet (initial creation is handled by another useEffect)
    if (!paymentIntentCreatedRef.current) {
      // Update refs for initial state tracking - only if they're different to prevent false change detection
      if (prevBulkPricingRef.current?.pricing?.finalPrice !== bulkPricing?.pricing?.finalPrice) {
        prevBulkPricingRef.current = bulkPricing;
      }
      if (prevAppliedPromoRef.current?.code !== appliedPromo?.code) {
        prevAppliedPromoRef.current = appliedPromo;
      }
      logger.debug('Discount change useEffect: Payment intent not created yet, skipping', {
        hasAppliedPromo: !!appliedPromo,
        promoCode: appliedPromo?.code,
        hasBulkPricing: !!bulkPricing
      });
      return;
    }
    
    // FIX #19: Extract stable values for comparison (use current values, not refs)
    const currentBulkPricingFinalPrice = bulkPricing?.pricing?.finalPrice;
    const currentBulkPricingDiscount = bulkPricing?.pricing?.discountAmount;
    const currentPromoCode = appliedPromo?.code;
    const currentPromoDiscount = appliedPromo?.discount_value;
    
    // Check if values actually changed (compare with previous ref values)
    const bulkPricingChanged = 
      (prevBulkPricingRef.current?.pricing?.finalPrice !== currentBulkPricingFinalPrice) ||
      (prevBulkPricingRef.current?.pricing?.discountAmount !== currentBulkPricingDiscount) ||
      (!!prevBulkPricingRef.current !== !!bulkPricing);
    
    const promoChanged = 
      (prevAppliedPromoRef.current?.code !== currentPromoCode) ||
      (prevAppliedPromoRef.current?.discount_value !== currentPromoDiscount) ||
      (!!prevAppliedPromoRef.current !== !!appliedPromo);
    
    // Debug logging
    logger.debug('Discount change useEffect triggered', {
      promoChanged,
      bulkPricingChanged,
      currentPromoCode,
      previousPromoCode: prevAppliedPromoRef.current?.code,
      hasAppliedPromo: !!appliedPromo,
      paymentIntentCreated: paymentIntentCreatedRef.current
    });
    
    // FIX #19: Update refs atomically AFTER comparison but BEFORE any async operations
    // CRITICAL: Only update refs if there was an actual change to prevent false change detection
    // This prevents the ref from being updated when bulk pricing is just being restored or when
    // the useEffect runs due to other dependency changes (like clientSecret)
    if (bulkPricingChanged) {
      prevBulkPricingRef.current = bulkPricing;
    }
    if (promoChanged) {
      prevAppliedPromoRef.current = appliedPromo;
    }
    
    // Only recreate if we have courses, payment intent is already created, AND there are actual discount changes
    // Also check that we're not already processing to prevent infinite loops
    // CRITICAL: Also check that we have a clientSecret (payment intent exists) before recreating
    // Only recreate if there's actually a discount applied (bulk pricing or promo)
    if (courses && Array.isArray(courses) && courses.length > 0 && 
        paymentIntentCreatedRef.current && 
        clientSecret && // CRITICAL: Only recreate if we already have a payment intent
        !processing && 
        (bulkPricingChanged || promoChanged)) {
      // FIX #5: Use single source of truth - getTotalPrice() function
      const totalAmount = getTotalPrice();
      
      logger.debug('Discount change detected, recreating payment intent', {
        appliedPromo: appliedPromo ? appliedPromo.code : null,
        bulkPricing: bulkPricing ? bulkPricing.pricing : null,
        totalAmount,
        bulkPricingChanged,
        promoChanged
      });
      // FIX #18: Increased debounce to prevent rapid-fire calls (was 500ms, now 1000ms)
      // FIX #1: Use refs to avoid stale closures - check current values at timeout execution
      // FIX: Ensure state is fully updated before recreating payment intent
      const timeoutId = setTimeout(() => {
        // Double-check we're not processing and payment intent exists before recreating
        // Use refs to get current values (not stale closure values)
        if (!processingRef.current && paymentIntentCreatedRef.current) {
          // FIX: Use refs to get current state values (avoid stale closures in setTimeout)
          const currentAppliedPromo = appliedPromoRef.current;
          const currentBulkPricing = bulkPricingRef.current;
          
          // Verify that the discount state is still valid before recreating
          // This ensures we don't recreate with stale state
          logger.debug('Recreating payment intent after discount change', {
            hasAppliedPromo: !!currentAppliedPromo,
            hasBulkPricing: !!currentBulkPricing,
            promoCode: currentAppliedPromo?.code,
            // Calculate total with current state values
            totalAmount: (() => {
              // FIX: Add null safety check for courses array
              const baseTotal = (courses && Array.isArray(courses) && courses.length > 0) ? courses.reduce((sum, course) => {
                if (!course) return sum;
                const price = (course.isPaidCourse || course.is_paid_course) ? (parseFloat(course.price) || 0) : 0;
                return Math.round((sum + price) * 100) / 100;
              }, 0) : 0;
              
              if (currentAppliedPromo) {
                if (currentAppliedPromo.discount_type === 'percentage') {
                  const discountValue = parseFloat(currentAppliedPromo.discount_value) || 0;
                  const discountAmount = baseTotal * (discountValue / 100);
                  return Math.max(0, Math.round((baseTotal - discountAmount) * 100) / 100);
                } else {
                  const discountValue = parseFloat(currentAppliedPromo.discount_value) || 0;
                  return Math.max(0, Math.round((baseTotal - discountValue) * 100) / 100);
                }
              } else if (currentBulkPricing && currentBulkPricing?.pricing?.finalPrice && !productIds) {
                return Math.round(parseFloat(currentBulkPricing.pricing.finalPrice) * 100) / 100;
              }
              return baseTotal;
            })()
          });
          // FIX: Use forceRecreate flag but don't clear clientSecret to prevent remount loop
          // The payment intent will be updated with the new amount
          createPaymentIntent(true);
        }
      }, 1000); // 1000ms debounce to prevent race conditions and ensure state is updated
      
      // FIX #7 & #19: Always return cleanup function to prevent memory leaks
      return () => {
        clearTimeout(timeoutId);
      };
    }
    
    // FIX #7 & #19: Return empty cleanup function if condition is false to maintain consistent hook behavior
    // This ensures cleanup is always called, preventing memory leaks
    return () => {
      // No timeout to clean up, but function exists for consistency
    };
    // FIX: Remove createPaymentIntent and getTotalPrice from dependencies to prevent infinite loop
    // These functions are memoized and stable, but including them causes loops when they're recreated
    // We use refs to access current values in setTimeout callbacks, so stale closures aren't an issue
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedPromo, bulkPricing, courses.length, processing]); // Removed createPaymentIntent and getTotalPrice to prevent loops



  // Helper function to format error messages
  const formatErrorMessage = (message) => {
    if (!message) return message;
    // Add period if it's a sentence and doesn't end with punctuation
    if (message.length > 0 && !message.match(/[.!?]$/)) {
      return message + '.';
    }
    return message;
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) return;
    
    // FIX #13: Add loading state for promo code application
    if (promoLoading) return; // Prevent multiple simultaneous requests
    
    // FIX: Check if bulk pricing is active first - prevent promo code application if it is
    // This saves processing by not making the API call if bulk pricing is already applied
    if (bulkPricing && bulkPricing.pricing && bulkPricing.pricing.savings > 0) {
      setPromoInfo(null); // Clear any info messages
      setPromoError('Promo code cannot be combined with existing discounts. Please remove the bulk discount.');
      logger.info('Promo code application blocked - bulk pricing is active', {
        promoCode: promoCode.trim().toUpperCase(),
        bulkPricingDiscount: bulkPricing.pricing.discountAmount
      });
      return; // Exit early - don't make API call
    }
    
    // FIX #31: Rate limiting - prevent rapid API calls
    const now = Date.now();
    const lastCallTime = apiCallTimestampsRef.current.applyPromoCode;
    if (lastCallTime && (now - lastCallTime) < RATE_LIMIT_DELAY) {
      logger.debug('Promo code application rate limited, skipping');
      return;
    }
    apiCallTimestampsRef.current.applyPromoCode = now;
    
    // Clear previous promo error (but keep success message if promo is already applied)
    setPromoError(null);
    // Only clear info message if applying a new code (not if one is already applied)
    if (!appliedPromo) {
      setPromoInfo(null);
    }
    setPromoLoading(true);
    
    try {
      // Ensure course IDs are integers (backend expects integers)
      // FIX: Add null safety checks for courses array
      const courseIds = productIds ? [] : (courses && Array.isArray(courses) ? courses.map(course => course && course.id ? parseInt(course.id) : null).filter(id => id !== null) : []);
      const productIdsArray = productIds ? (courses && Array.isArray(courses) ? courses.map(course => course && course.id ? parseInt(course.id) : null).filter(id => id !== null) : []) : [];
      
      logger.debug('Applying promo code', {
        code: promoCode.trim().toUpperCase(),
        productIds,
        courseIds,
        productIdsArray,
        courses: courses.map(c => ({ id: c.id, name: c.name }))
      });
      
      const response = await api.validatePromoCode(promoCode.trim().toUpperCase(), courseIds, productIdsArray);
      
      logger.debug('Promo code validation response', { response });
      
      if (response.success) {
        // Clear bulk pricing when promo code is applied (mutually exclusive)
        // Note: We already checked for bulk pricing above, but clear it here as a safety measure
        setBulkPricing(null);
        logger.info('Promo code applied, bulk pricing cleared');
        
        // FIX: Set appliedPromo state first, then let the discount change useEffect handle payment intent recreation
        // This ensures the state is updated before the payment intent is recreated
        setAppliedPromo({
          code: response.promoCode.code,
          discount_type: response.promoCode.discount_type,
          discount_value: response.promoCode.discount_value,
          description: response.promoCode.description,
          success_message: response.promoCode.success_message
        });
        setPromoCode('');
        // Display success message only if it's configured and not empty
        // Check for null, undefined, and empty strings
        const successMessage = response.promoCode.success_message;
        if (successMessage && successMessage.trim().length > 0) {
          setPromoInfo(successMessage.trim());
        } else {
          setPromoInfo(null);
        }
        
        // FIX #7: The discount change useEffect will automatically detect the promo code change
        // and recreate the payment intent with the correct discounted amount
        // No need to manually trigger here - the useEffect will handle it
        logger.debug('Promo code applied, discount change useEffect will recreate payment intent', {
          promoCode: response.promoCode.code,
          discountType: response.promoCode.discount_type,
          discountValue: response.promoCode.discount_value
        });
      } else {
        setPromoError(formatErrorMessage(response.message || 'Invalid promo code'));
        setPromoInfo(null); // Clear info message on error
      }
    } catch (error) {
      logger.error('Error applying promo code', { error, code: promoCode, errorMessage: error.message, errorResponse: error.response });
      // FIX #11 & #20: Distinguish between network errors, timeout errors, and server errors
      const isTimeoutError = error.isTimeout || error.message?.includes('timeout') || error.name === 'AbortError';
      const isNetworkError = !error.response && (error.message?.includes('fetch') || error.message?.includes('network'));
      const errorMessage = isTimeoutError
        ? 'Request timed out. Please check your internet connection and try again.'
        : isNetworkError 
        ? 'Network error. Please check your connection and try again.'
        : (error.response?.data?.message || error.message || 'Failed to apply promo code. Please try again.');
      setPromoError(formatErrorMessage(errorMessage));
    } finally {
      // FIX #13: Clear loading state
      setPromoLoading(false);
    }
  };

  // Remove bulk pricing discount
  const removeBulkPricing = async () => {
    // Only remove if bulk pricing is actually applied
    if (!bulkPricing) {
      return; // No bulk pricing to remove
    }
    
    // FIX #31: Rate limiting - prevent rapid API calls
    const now = Date.now();
    const lastCallTime = apiCallTimestampsRef.current.removePromoCode; // Reuse same rate limit
    if (lastCallTime && (now - lastCallTime) < RATE_LIMIT_DELAY) {
      logger.debug('Bulk pricing removal rate limited, skipping');
      return;
    }
    apiCallTimestampsRef.current.removePromoCode = now;
    
    // Clear bulk pricing state
    prevBulkPricingRef.current = null;
    setBulkPricing(null);
    
    // Clear from sessionStorage
    if (typeof sessionStorage !== 'undefined') {
      const paramsKey = `${courseId || ''}_${courseIds || ''}_${productIds || ''}`;
      const bulkPricingKey = `paymentForm_bulkPricing_${paramsKey}`;
      sessionStorage.removeItem(bulkPricingKey);
    }
    
    logger.info('Bulk pricing removed');
    // The discount change useEffect will automatically detect the bulk pricing change
    // and recreate the payment intent with the correct amount (without bulk discount)
  };

  // FIX #1 & #5: Make removePromoCode async with proper error handling
  // FIX #37: Already async - verified complete implementation
  const removePromoCode = async () => {
    // FIX #18: Add validation - only recalculate if promo was actually applied
    if (!appliedPromo) {
      return; // No promo to remove
    }
    
    // FIX #31: Rate limiting - prevent rapid API calls
    const now = Date.now();
    const lastCallTime = apiCallTimestampsRef.current.removePromoCode;
    if (lastCallTime && (now - lastCallTime) < RATE_LIMIT_DELAY) {
      logger.debug('Promo code removal rate limited, skipping');
      return;
    }
    apiCallTimestampsRef.current.removePromoCode = now;
    
    setAppliedPromo(null);
    setPromoError(null); // Clear any promo errors
    setPromoInfo(null); // Clear success message when promo code is removed
    
    // Recalculate bulk pricing when promo code is removed (if applicable)
    if (courses && Array.isArray(courses) && courses.length > 1 && !productIds) {
      setBulkPricingLoading(true); // FIX #14: Set loading state
      try {
        // FIX #13: Check if component is still mounted
        if (abortControllerRef.current?.signal.aborted) {
          logger.debug('Bulk pricing recalculation cancelled - component unmounted');
          return;
        }
        
        const courseIdsForBulk = courses.filter(course => course && course.id).map(course => parseInt(course.id));
        const bulkResponse = await api.calculateBulkPricing(courseIdsForBulk);
        
        // FIX #13: Check again if component is still mounted before setting state
        if (abortControllerRef.current?.signal.aborted) {
          logger.debug('Bulk pricing response received but component unmounted - ignoring');
          return;
        }
        
        if (bulkResponse.success && bulkResponse.bulkPricing) {
          // CRITICAL: Not restoring - this is a fresh calculation, so update prevRef
          prevBulkPricingRef.current = bulkResponse;
          setBulkPricing(bulkResponse);
          // Store bulk pricing in sessionStorage for future remounts
          if (typeof sessionStorage !== 'undefined') {
            const paramsKey = `${courseId || ''}_${courseIds || ''}_${productIds || ''}`;
            const bulkPricingKey = `paymentForm_bulkPricing_${paramsKey}`;
            sessionStorage.setItem(bulkPricingKey, JSON.stringify(bulkResponse));
          }
          logger.info('Bulk pricing recalculated after promo code removal');
        } else {
          setBulkPricing(null);
          logger.debug('No bulk pricing available after promo code removal');
        }
      } catch (error) {
        // FIX #5: Proper error handling - log and clear bulk pricing
        // FIX #13: Don't set state if component is unmounted
        if (abortControllerRef.current?.signal.aborted) {
          logger.debug('Error occurred but component unmounted - ignoring');
          return;
        }
        logger.error('Error recalculating bulk pricing after promo removal', { error });
        setBulkPricing(null);
        // Don't show error to user - bulk pricing is optional
      } finally {
        // FIX #14: Clear loading state (only if component is still mounted)
        if (!abortControllerRef.current?.signal.aborted) {
          setBulkPricingLoading(false);
        }
      }
    } else {
      // Clear bulk pricing if not applicable
      setBulkPricing(null);
      setBulkPricingLoading(false);
    }
  };






  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // FIX #9: Prevent duplicate payment submissions
    if (processing || success) {
      logger.debug('Payment submission blocked - already processing or succeeded');
      return;
    }
    
    if (!stripe || !elements) {
      return;
    }
    
    // LAZY CREATION: Create payment intent if it doesn't exist
    // This ensures payment intent is only created when user actually clicks "Pay"
    let currentClientSecret = clientSecret || clientSecretFromParent;
    if (!currentClientSecret) {
      logger.info('Creating payment intent on Pay button click (lazy creation)');
      setProcessing(true); // Show loading state
      try {
        // Create payment intent and wait for it to complete
        await createPaymentIntent(true); // Force creation
        
        // The createPaymentIntent function sets clientSecret via setState (async)
        // We need to wait for React to update the state
        // Use a small delay and then check - the state should be updated by then
        await new Promise(resolve => setTimeout(resolve, 200));
        
        // Re-check clientSecret after state update
        // Note: We can't directly access updated state in same function, but React will have updated it
        // If still not available, the payment will fail below and user can retry
        currentClientSecret = clientSecret || clientSecretFromParent;
        
        if (!currentClientSecret) {
          // If still not available, it might be a timing issue
          // Log warning but continue - the payment confirmation will fail gracefully if needed
          logger.warn('Payment intent created but clientSecret not immediately available in state');
          // Don't return here - let the payment flow continue and handle the error below
        } else {
          logger.info('Payment intent created successfully on Pay button click');
        }
      } catch (createError) {
        logger.error('Failed to create payment intent on Pay button click', { error: createError });
        setError(formatErrorMessage('Failed to initialize payment. Please try again.'));
        setProcessing(false);
        return;
      }
    }
    
    // Final check - if we still don't have clientSecret, show error
    currentClientSecret = clientSecret || clientSecretFromParent;
    if (!currentClientSecret) {
      logger.error('No clientSecret available for payment');
      setError(formatErrorMessage('Payment initialization failed. Please refresh the page and try again.'));
      setProcessing(false);
      return;
    }
    
    // FIX #15: Check payment intent status before submit
    // FIX #33: Payment intent ownership validation - backend should validate that payment intent
    // belongs to the current user. Frontend cannot fully prevent manipulation, but we validate
    // status to ensure payment intent is in a valid state before confirmation.
    if (currentClientSecret) {
      try {
        const { paymentIntent } = await stripe.retrievePaymentIntent(currentClientSecret);
        if (paymentIntent) {
          // FIX #22: Check if payment intent already succeeded (prevent duplicate confirmation)
          if (paymentIntent.status === 'succeeded') {
            logger.warn('Payment intent already succeeded, redirecting to confirmation');
            // Payment already succeeded, navigate to confirmation
            navigate('/confirmation', {
              state: { 
                purchaseData: { 
                  success: true, 
                  message: 'Payment already completed',
                  paymentIntentId: paymentIntent.id
                } 
              }
            });
            return;
          }
          
          // FIX #16: Handle canceled payment intents
          if (paymentIntent.status === 'canceled') {
            setError(formatErrorMessage('Payment was canceled. Please create a new payment.'));
            setClientSecret(null);
            setPaymentIntentCreated(false);
            return;
          }
        }
      } catch (retrieveError) {
        logger.error('Error checking payment intent status before submit', { error: retrieveError });
        // Continue with submission - might be a transient error
      }
    }

    // FIX #12: Clear all error states at start of submit
    setError(null);
    setPromoError(null);
    
    // FIX #8: Validate course prices haven't changed since payment intent creation
    const priceMismatches = [];
    courses.forEach(course => {
      const initialPrice = initialCoursePricesRef.current.get(course.id);
      const currentPrice = parseFloat(course.price) || 0;
      if (initialPrice !== undefined && Math.abs(initialPrice - currentPrice) > 0.01) {
        priceMismatches.push({
          courseId: course.id,
          courseName: course.name,
          initialPrice,
          currentPrice
        });
      }
    });
    
    if (priceMismatches.length > 0) {
      logger.warn('Course price mismatch detected', { priceMismatches });
      const mismatchNames = priceMismatches.map(m => m.courseName).join(', ');
      setError(formatErrorMessage(
        `The price for ${mismatchNames} has changed. Please refresh the page and try again.`
      ));
      // Clear payment intent to force recreation with new prices
      setClientSecret(null);
      setPaymentIntentCreated(false);
      return;
    }
    
    // FIX #25: Validate payment method before submit
    try {
      // Check if payment method is complete
      const { error: elementsError } = await elements.submit();
      if (elementsError) {
        logger.warn('Payment method validation failed', { error: elementsError });
        setError(formatErrorMessage(elementsError.message || 'Please complete your payment method details.'));
        return;
      }
    } catch (validationError) {
      logger.error('Error validating payment method', { error: validationError });
      // Continue anyway - Stripe will handle validation during confirmation
    }
    
    setProcessing(true);

    try {
      // Confirm Payment with unified Payment Element
      // Use currentClientSecret which is either clientSecret or clientSecretFromParent
      const currentClientSecretForPayment = clientSecret || clientSecretFromParent;
      const { error: stripeError } = await stripe.confirmPayment({
        elements,
        clientSecret: currentClientSecretForPayment,
        confirmParams: {
          return_url: window.location.origin + '/portal/confirmation'
        }
      });

      if (stripeError) {
        // FIX #11: Use formatErrorMessage for consistent error message formatting
        // Enhanced error messages for different failure types
        let errorMessage = stripeError.message;
        
        // Provide more user-friendly messages for common failure codes
        if (stripeError.code) {
          switch (stripeError.code) {
            case 'card_declined':
              errorMessage = 'Your card was declined. Please try a different payment method or contact your bank.';
              break;
            case 'insufficient_funds':
              errorMessage = 'Your card has insufficient funds. Please try a different payment method.';
              break;
            case 'expired_card':
              errorMessage = 'Your card has expired. Please use a different card.';
              break;
            case 'incorrect_cvc':
              errorMessage = 'Your card\'s security code is incorrect. Please check and try again.';
              break;
            case 'incorrect_number':
              errorMessage = 'Your card number is incorrect. Please check and try again.';
              break;
            case 'processing_error':
              errorMessage = 'An error occurred while processing your payment. Please try again.';
              break;
            case 'generic_decline':
              errorMessage = 'Your card was declined. Please try a different payment method.';
              break;
            default:
              // Use Stripe's error message if no specific handling
              errorMessage = stripeError.message || 'Payment failed. Please try again.';
          }
        }
        
        logger.warn('Stripe payment error', {
          errorCode: stripeError.code,
          errorMessage: stripeError.message,
          errorType: stripeError.type
        });
        
        setError(formatErrorMessage(errorMessage));
        setProcessing(false);
        return; // CRITICAL: Return early - enrollment will NOT be created
      }

      // FIX #5: Removed redundant clientSecret check - if it was falsy, confirmPayment would have already failed
      // The check is unnecessary since we already used clientSecret in confirmPayment above
      
      // For cards and wallets with no redirect, poll retrievePaymentIntent to check status
      // Note: paymentIntent was already retrieved in the pre-submit check, but retrieve again
      // to get the latest status after confirmPayment
      let paymentIntent;
      try {
        const result = await stripe.retrievePaymentIntent(currentClientSecretForPayment);
        paymentIntent = result.paymentIntent;
        
        // FIX #20 & #10: Validate payment intent status and handle all statuses
        if (!paymentIntent) {
          setError(formatErrorMessage('Could not retrieve payment intent. Please try again.'));
          setProcessing(false);
          return;
        }
      } catch (retrieveError) {
        logger.error('Error retrieving payment intent after confirmation', { error: retrieveError });
        setError(formatErrorMessage('Could not retrieve payment intent. Please try again.'));
        setProcessing(false);
        return;
      }
      
      // FIX #10 & #11: Handle all payment intent statuses with consistent error formatting
      // CRITICAL: Enrollment will NOT be created for any status other than 'succeeded'
      switch (paymentIntent.status) {
        case 'requires_payment_method':
          setError(formatErrorMessage('Please complete your payment method details.'));
          setProcessing(false);
          return; // No enrollment
          
        case 'requires_action':
        case 'requires_confirmation':
          // Payment requires additional action (3D Secure, etc.)
          // Stripe Elements will handle this automatically, but we should inform user
          logger.debug('Payment requires additional action', { status: paymentIntent.status });
          // Continue - Stripe will handle the action and redirect if needed
          break;
          
        case 'processing':
          setError(formatErrorMessage('Your payment is being processed. Please wait...'));
          setProcessing(false);
          return; // No enrollment - payment not complete
          
        case 'requires_capture':
          setError(formatErrorMessage('Payment requires capture. Please contact support.'));
          setProcessing(false);
          return; // No enrollment
          
        case 'canceled':
          // FIX #16: Handle payment intent cancellation
          setError(formatErrorMessage('Payment was canceled. Please try again.'));
          setProcessing(false);
          // Clear client secret to allow new payment intent creation
          setClientSecret(null);
          setPaymentIntentCreated(false);
          return; // No enrollment
          
        case 'failed':
          // CRITICAL: Payment failed - show user-friendly error message
          const failureMessage = paymentIntent.last_payment_error?.message || 'Your payment was declined.';
          const failureCode = paymentIntent.last_payment_error?.code;
          
          let userFriendlyMessage = failureMessage;
          if (failureCode) {
            switch (failureCode) {
              case 'card_declined':
                userFriendlyMessage = 'Your card was declined. Please try a different payment method or contact your bank.';
                break;
              case 'insufficient_funds':
                userFriendlyMessage = 'Your card has insufficient funds. Please try a different payment method.';
                break;
              case 'expired_card':
                userFriendlyMessage = 'Your card has expired. Please use a different card.';
                break;
              case 'incorrect_cvc':
                userFriendlyMessage = 'Your card\'s security code is incorrect. Please check and try again.';
                break;
              case 'incorrect_number':
                userFriendlyMessage = 'Your card number is incorrect. Please check and try again.';
                break;
              case 'generic_decline':
                userFriendlyMessage = 'Your card was declined. Please try a different payment method.';
                break;
              default:
                // Use the failure message from Stripe if no specific handling
                break;
            }
          }
          
          logger.warn('Payment failed', {
            paymentIntentId: paymentIntent.id,
            failureCode,
            failureMessage: paymentIntent.last_payment_error?.message
          });
          
          setError(formatErrorMessage(userFriendlyMessage));
          setProcessing(false);
          // Clear client secret to allow new payment intent creation
          setClientSecret(null);
          setPaymentIntentCreated(false);
          return; // CRITICAL: No enrollment for failed payments
          
        case 'succeeded':
          // Continue to confirmation - enrollment will be created
          break;
          
        default:
          logger.warn('Unknown payment intent status', { status: paymentIntent.status });
          setError(formatErrorMessage(`Payment status: ${paymentIntent.status}. Please contact support if this persists.`));
          setProcessing(false);
          return; // No enrollment for unknown status
      }
      
      if (paymentIntent.status === 'succeeded') {
        // CRITICAL FIX #3: Improved error recovery for backend confirmation failures
        let confirmationResponse;
        try {
          if (productIds) {
            confirmationResponse = await api.confirmProductPayment({
              paymentIntentId: paymentIntent.id
            });
          } else {
            confirmationResponse = await api.confirmPayment({
              paymentIntentId: paymentIntent.id
            });
          }

          // Verify confirmation was successful
          if (!confirmationResponse || !confirmationResponse.success) {
            throw new Error(confirmationResponse?.message || 'Backend confirmation failed');
          }

          setSuccess(true);
          
          // Debug: Log the confirmation response
          logger.debug('Confirmation response', { 
            confirmationResponse,
            redirectTo: confirmationResponse.redirectTo,
            productIds 
          });
        
          // Handle different redirect scenarios based on API response
          logger.debug('About to redirect', { redirectTo: confirmationResponse.redirectTo });
          if (confirmationResponse.redirectTo === 'confirmation') {
            logger.debug('Redirecting to confirmation page');
            // Product purchase - show confirmation page
            navigate('/confirmation', {
              state: { purchaseData: confirmationResponse }
            });
          } else {
            logger.debug('Redirecting to confirmation page for course enrollment');
            // Course enrollment - also show confirmation page
            navigate('/confirmation', {
              state: { purchaseData: confirmationResponse }
            });
          }
        } catch (confirmationError) {
          // CRITICAL FIX #3: Handle backend confirmation failure after Stripe success
          logger.error('Backend confirmation failed after Stripe payment succeeded', {
            error: confirmationError,
            paymentIntentId: paymentIntent.id,
            paymentIntentStatus: paymentIntent.status
          });
          
          // Payment succeeded in Stripe but backend confirmation failed
          // Show error with payment intent ID for support reference
          // FIX #11: Use formatErrorMessage for consistent formatting
          setError(formatErrorMessage(
            `Payment processed successfully, but enrollment confirmation failed. ` +
            `Please contact support with payment ID: ${paymentIntent.id}. ` +
            `Your payment has been received and will be processed manually.`
          ));
          
          // Don't set success to true since enrollment didn't complete
          // User will need to contact support
        }
      } else if (paymentIntent && paymentIntent.status !== 'succeeded') {
        // Payment intent exists but hasn't succeeded yet
        logger.warn('Payment intent not succeeded', { 
          status: paymentIntent.status,
          paymentIntentId: paymentIntent.id 
        });
        // FIX #11: Use formatErrorMessage for consistent formatting
        setError(formatErrorMessage(`Payment status: ${paymentIntent.status}. Please try again or contact support.`));
      }
    } catch (error) {
      logger.error('Payment error', { error });
      // FIX #11: Use formatErrorMessage for consistent error message formatting
      // FIX #20: Distinguish between network errors, timeout errors, and server errors
      const isTimeoutError = error.isTimeout || error.message?.includes('timeout') || error.name === 'AbortError';
      const isNetworkError = !error.response && (error.message?.includes('fetch') || error.message?.includes('network'));
      const errorMessage = isTimeoutError
        ? 'Request timed out. Please check your internet connection and try again.'
        : isNetworkError
        ? 'Network error. Please check your connection and try again.'
        : (error.response?.data?.message || error.message || 'Payment failed. Please try again.');
      setError(formatErrorMessage(errorMessage));
    } finally {
      setProcessing(false);
    }
  };

  // FIX #36: Move logger calls out of render to prevent unnecessary re-renders
  // Logger calls moved to useEffect hooks where appropriate

  if (loading) {
    return (
      <div className={styles.container}>
        <motion.div
          className={styles.paymentCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className={styles.loadingSpinner} />
            <p style={{ marginTop: '1rem', color: 'var(--fi-muted)' }}>
              Loading course information...
            </p>
          </div>
        </motion.div>
      </div>
    );
  }

  // FIX #21 & #30: Better handling of empty courses array edge case
  // FIX: Add null safety check and prevent render errors
  if (!courses || !Array.isArray(courses) || courses.length === 0) {
    // FIX: Safely get error message to prevent render errors
    let errorMessage = '';
    try {
      errorMessage = error || (productIds 
        ? 'Product(s) not found or not available for purchase. Please check the product codes and try again.' 
        : 'Course(s) not found or not available for enrollment. Please check the course codes and try again.');
    } catch (e) {
      logger.error('Error getting error message', { e });
      errorMessage = 'An error occurred while loading course information. Please try again.';
    }
    
    logger.warn('No courses found', { courseId, courseIds, productIds, loading, error: errorMessage });
    return (
      <div className={styles.container}>
        <motion.div
          className={styles.paymentCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className={styles.backButtonContainer}>
            <button className={styles.backButton} onClick={() => navigate('/catalog')} aria-label="Back to course catalog">
              <FiArrowLeft size={16} />
              Back to Course Catalog
            </button>
          </div>
          <motion.div
            className={styles.errorMessage}
            role="alert"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.errorIcon}>
              <FiAlertCircle />
            </div>
            <div className={styles.errorText}>
              {errorMessage}
            </div>
          </motion.div>
          {!error && (
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                If you believe this is an error, please contact support.
              </p>
            </div>
          )}
        </motion.div>
      </div>
    );
  }

  if (success) {
    return (
      <div className={styles.container}>
        <motion.div
          className={styles.paymentCard}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className={styles.successMessage}>
            <FiCheck size={20} />
            Payment successful! You've been {productIds ? 'granted access to' : 'enrolled in'} {courses && courses.length > 0 ? courses.length : 0} {productIds ? 'product' : 'course'}{(courses && courses.length !== 1) ? 's' : ''}.
            Redirecting to {productIds ? 'confirmation page' : 'My Courses'}...
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <motion.div
        className={styles.paymentCard}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className={styles.backButtonContainer}>
          <button 
            className={styles.backButton}
            onClick={() => navigate('/catalog')}
            aria-label="Back to course catalog"
            type="button"
          >
            <FiArrowLeft size={16} />
            Back to Course Catalog
          </button>
        </div>

        <div className={styles.header}>
          <h1 className={styles.title}>{productIds ? 'Product Purchase' : 'Course Enrollment'}</h1>
          <p className={styles.subtitle}>Complete your payment to {productIds ? 'purchase' : 'enroll in'} {courses && courses.length > 0 ? courses.length : 0} {productIds ? 'product' : 'course'}{(courses && courses.length !== 1) ? 's' : ''}</p>
        </div>

        {/* Product Summary Card */}
        <div className={styles.productSummaryCard}>
          <div className={styles.productList}>
          {courses && courses.length > 0 ? courses.map((course) => {
            // FIX: Add null checks to prevent errors if course data is incomplete
            if (!course || !course.id) {
              logger.warn('Invalid course data in courses array', { course });
              return null;
            }
            return (
              <div key={course.id} className={styles.productItem}>
                <div className={styles.productInfo}>
                  <h4 className={styles.productName}>{course.name || 'Unnamed Course'}</h4>
                  {!productIds && (
                    <span className={styles.productCode}>
                      {`${course.code || 'N/A'} • ${course.instructor || 'TBA'}`}
                    </span>
                  )}
                </div>
                <span className={styles.productPrice}>
                  {course.isPaidCourse ? `$${course.price || 0}` : 'Free'}
                </span>
              </div>
            );
          }) : (
            <div className={styles.productItem}>
              <div className={styles.productInfo}>
                <h4 className={styles.productName}>No courses found</h4>
              </div>
            </div>
          )}
          </div>
        </div>

        {/* FIX #27: Simplified bulk pricing display logic */}
        {/* FIX #27: Simplify bulk pricing display logic - check all conditions in one place */}
        {courses.length > 1 && 
         bulkPricing?.bulkPricing?.name && 
         bulkPricing?.pricing?.savings && 
         bulkPricing.pricing.savings > 0 &&
         !appliedPromo && 
         !productIds && (
          <div className={styles.bulkPricingInfo} role="status" aria-live="polite">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h4 className={styles.bulkPricingTitle} style={{ margin: 0 }}>🎉 Bulk Discount Applied!</h4>
              <button 
                className={styles.removePromoButton}
                onClick={removeBulkPricing}
                aria-label="Remove bulk discount"
                type="button"
              >
                Remove
              </button>
            </div>
            <div className={styles.bulkPricingDetails}>
              <span>{bulkPricing.bulkPricing.name}</span>
              <span>Save ${(bulkPricing.pricing.savings || 0).toFixed(2)}</span>
            </div>
          </div>
        )}

        {/* Promo Code Card */}
        <div className={styles.promoCard}>
          <h3 className={styles.promoCardTitle}>Promo code</h3>
          {appliedPromo ? (
            <div className={styles.appliedPromo} role="status" aria-live="polite">
              <div className={styles.promoInfo}>
                <span className={styles.promoCode} aria-label={`Applied promo code: ${appliedPromo.code}`}>{appliedPromo.code}</span>
                <span className={styles.promoDiscount}>
                  {appliedPromo.discount_type === 'percentage' 
                    ? `${parseFloat(appliedPromo.discount_value).toFixed(0)}% off`
                    : `$${appliedPromo.discount_value} off`
                  }
                </span>
              </div>
              <button 
                className={styles.removePromoButton}
                onClick={removePromoCode}
                aria-label={`Remove promo code ${appliedPromo.code}`}
                type="button"
              >
                Remove
              </button>
            </div>
          ) : (
            <div className={styles.promoInput}>
              <input
                className={styles.promoCodeInput}
                id="promo-code-input"
                type="text"
                placeholder="Enter promo code"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value);
                  if (promoError) setPromoError(null);
                  // Only clear success message if user is typing a new code (not when promo is already applied)
                  if (promoInfo && !appliedPromo) {
                    setPromoInfo(null);
                  }
                }}
                onKeyPress={(e) => e.key === 'Enter' && applyPromoCode()}
                aria-label="Promo code input"
                aria-describedby={promoError ? "promo-error" : undefined}
              />
              <button 
                className={styles.promoButton}
                onClick={applyPromoCode} 
                disabled={!promoCode.trim() || promoLoading}
                aria-label="Apply promo code"
                type="button"
              >
                {promoLoading ? 'Applying...' : 'Apply'}
              </button>
            </div>
          )}
        </div>

        {promoError && (
          <motion.div
            className={styles.promoErrorMessage}
            id="promo-error" 
            role="alert" 
            aria-live="assertive"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className={styles.promoErrorIcon}>
              <FiAlertCircle />
            </div>
            <div className={styles.promoErrorText}>{promoError}</div>
          </motion.div>
        )}
        {promoInfo && (
          <motion.div
            className={styles.promoInfoMessage}
            id="promo-info" 
            role="status" 
            aria-live="polite"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.promoInfoIcon}>
              <FiCheckCircle />
            </div>
            <div className={styles.promoInfoText}>{promoInfo}</div>
          </motion.div>
        )}

        {/* Order Summary Card */}
        <div className={styles.orderSummaryCard} role="region" aria-label="Payment summary">
          <h3 className={styles.orderSummaryTitle}>Order summary</h3>
          <div className={styles.totalRow}>
            <span className={styles.totalLabel}>Subtotal:</span>
            <span className={styles.totalValue} aria-label={`Subtotal: $${(courses.reduce((sum, course) => {
                const isPaid = course.isPaidCourse || course.is_paid_course;
                const price = isPaid ? (parseFloat(course.price) || 0) : 0;
                // FIX #23: Use Math.round to prevent floating point precision errors
                return Math.round((sum + price) * 100) / 100;
              }, 0) || 0).toFixed(2)}`}>
              {/* FIX #11: Use getTotalPrice() for consistent calculation (base only, no discounts) */}
              {/* FIX #23: Add precision handling to prevent floating point errors */}
              ${(courses.reduce((sum, course) => {
                const isPaid = course.isPaidCourse || course.is_paid_course;
                const price = isPaid ? (parseFloat(course.price) || 0) : 0;
                // FIX #23: Use Math.round to prevent floating point precision errors
                return Math.round((sum + price) * 100) / 100;
              }, 0) || 0).toFixed(2)}
            </span>
          </div>
          {/* FIX #6: Add null checks for bulk pricing display */}
          {bulkPricing?.pricing?.savings && !appliedPromo && (
            <div className={styles.totalRow}>
              <span className={styles.totalLabel}>Bulk Discount:</span>
              <span className={`${styles.totalValue} ${styles.discountValue}`}>
                -${(bulkPricing.pricing.savings || 0).toFixed(2)}
              </span>
            </div>
          )}
          {appliedPromo && (
            <div className={styles.totalRow}>
              <span className={styles.totalLabel}>Promo Code ({appliedPromo.code}):</span>
              <span className={`${styles.totalValue} ${styles.discountValue}`}>
                -${appliedPromo.discount_type === 'percentage' 
                  ? ((courses.reduce((sum, course) => sum + (parseFloat(course.price) || 0), 0) * (parseFloat(appliedPromo.discount_value) || 0)) / 100).toFixed(2)
                  : (parseFloat(appliedPromo.discount_value) || 0).toFixed(2)
                }
              </span>
            </div>
          )}
          <div className={styles.totalRow}>
            <span className={styles.totalLabel}>Total:</span>
            {/* FIX #9: Use memoized total price instead of calling function in render */}
            <span className={styles.totalValue} aria-label={`Total amount: $${(totalPrice || 0).toFixed(2)}`}>
              ${(totalPrice || 0).toFixed(2)}
            </span>
          </div>
        </div>

        {error && (
          <motion.div
            className={styles.errorMessage}
            role="alert"
            aria-live="assertive"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.errorIcon}>
              <FiAlertCircle />
            </div>
            <div className={styles.errorText}>
              {error}
            </div>
          </motion.div>
        )}

        {/* Show "Proceed to Payment" or "Complete Enrollment" button if payment form is not shown yet */}
        {!showPaymentForm ? (
          <button
            className={styles.submitButton}
            type="button"
            onClick={handleProceedToPayment}
            disabled={creatingPaymentIntent || processing || !courses || courses.length === 0}
          >
            {creatingPaymentIntent ? (
              <>{totalPrice === 0 ? 'Completing enrollment...' : 'Creating secure payment...'}</>
            ) : (
              <>
                <FiCreditCard size={18} />
                {totalPrice === 0 ? 'Complete Enrollment' : 'Proceed to Payment'}
              </>
            )}
          </button>
        ) : (
          <form className={styles.form} onSubmit={handleSubmit}>
            {/* FIX #9: Disable submit button during processing to prevent duplicate payments */}
            {/* Payment Card */}
            <div className={styles.paymentCard}>
              <div className={styles.paymentSectionTitle}>
                <FiCreditCard size={16} />
                Payment information
              </div>
              <p className={styles.paymentHelperText}>Pay with card</p>
              <div className={styles.cardElementContainer}>
            
                {/* Express Checkout (Apple Pay, Google Pay, Link) */}
                {/* FIX #25: Payment method validation handled by Stripe Elements */}
                {paymentIntentCreated && clientSecret && (
                  <div className={styles.expressCheckoutWrapper}>
                    <ExpressCheckoutElement
                        key={clientSecret} // Force re-render when client secret changes
                        options={{
                          buttonType: {
                            applePay: 'check-out',
                            googlePay: 'buy',
                            paypal: 'pay',
                            // FIX: Removed 'link' from buttonType - not a recognized parameter
                          },
                          buttonHeight: 48,
                          buttonTheme: {
                            applePay: 'black',
                            googlePay: 'black',
                            paypal: 'black',
                            // FIX: Removed 'link' from buttonTheme - not a recognized parameter
                          },
                          layout: {
                            overflow: 'never'
                          },
                          // FIX: Replaced legacy 'wallets' with 'paymentMethods' as recommended by Stripe
                          paymentMethods: {
                            applePay: 'auto',
                            googlePay: 'always',  // Changed from 'auto' to 'always' to force Google Pay to appear
                            paypal: 'auto',
                            link: 'auto'
                          }
                        }}
                        onReady={(event) => {
                          logger.debug('Express checkout ready', { 
                            availablePaymentMethods: event?.availablePaymentMethods,
                            event 
                          });
                        }}
                        onError={(event) => {
                          logger.error('Express checkout error', { error: event?.error?.message || event });
                          setError(event?.error?.message || 'Link or wallet payment failed. Please try another method.');
                        }}
                        onConfirm={async (event) => {
                        try {

                          setProcessing(true);
                          setError(null);

                          logger.debug('Express checkout onConfirm triggered', { 
                            event,
                            eventKeys: event ? Object.keys(event) : [],
                            productIds,
                            hasToken: !!localStorage.getItem('faithinsight_token'),
                            hasStripe: !!stripe,
                            hasElements: !!elements
                          });

                          // For Express Checkout, we need to explicitly confirm the payment with Stripe
                          // The onConfirm callback fires BEFORE Stripe processes the payment
                          
                          
                          // Confirm the payment with Stripe
                          const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
                            elements,
                            clientSecret,
                            redirect: 'if_required', // Don't redirect for Express Checkout
                            confirmParams: {
                              return_url: window.location.origin + '/confirmation'
                            }
                          });
                          
                          
                          if (confirmError) {
                            logger.error('Stripe confirmPayment error', { 
                              error: confirmError,
                              code: confirmError.code,
                              message: confirmError.message
                            });
                            throw new Error(confirmError.message || 'Payment confirmation failed');
                          }
                          
                          if (!paymentIntent || paymentIntent.status !== 'succeeded') {
                            logger.error('Payment not succeeded after Stripe confirmation', {
                              status: paymentIntent?.status
                            });
                            throw new Error(`Payment failed with status: ${paymentIntent?.status || 'unknown'}`);
                          }
                          
                          
                          // Extract payment intent ID for backend confirmation
                          const paymentIntentId = paymentIntent.id;
                          
                          logger.debug('Payment confirmed with Stripe, proceeding to backend', { 
                            paymentIntentId,
                            status: paymentIntent.status
                          });


                          // Now confirm payment on backend (API service handles token refresh automatically)
                          let confirmationResponse;
                          if (productIds) {
                            confirmationResponse = await api.confirmProductPayment({
                              paymentIntentId: paymentIntentId
                            });
                          } else {
                            confirmationResponse = await api.confirmPayment({
                              paymentIntentId: paymentIntentId
                            });
                          }

                          
                          logger.debug('Express checkout confirmation successful', { confirmationResponse });

                          setSuccess(true);
                          setProcessing(false);
                          
                          // Navigate to confirmation page
                          navigate('/confirmation', {
                            state: { purchaseData: confirmationResponse }
                          });
                        } catch (error) {
                          
                          logger.error('Express checkout error', { 
                            error, 
                            errorMessage: error?.message,
                            errorStatus: error?.status,
                            csrfTokenMissing: error?.csrfTokenMissing,
                            isNetworkError: error?.isNetworkError
                          });
                          
                          // Provide specific error messages
                          let errorMsg = 'Payment failed. Please try again.';
                          if (error?.status === 401) {
                            errorMsg = 'Your session has expired. Please refresh the page and try again.';
                          } else if (error?.csrfTokenMissing) {
                            errorMsg = 'Security token missing. Please refresh the page and try again.';
                          } else if (error?.isNetworkError || error?.isTimeout) {
                            errorMsg = 'Network error. Please check your connection and try again.';
                          } else if (error?.data?.message) {
                            errorMsg = error.data.message;
                          } else if (error?.message) {
                            errorMsg = error.message;
                          }
                          
                          
                          setError(errorMsg);
                          setProcessing(false);
                        }
                      }}
                      />
                    <div className={styles.orDivider}>or pay with card</div>
                  </div>
                )}
                
                {paymentIntentCreated && clientSecret ? (
                  <>
                    {/* FIX #4: PaymentElement layout set to accordion (matches comment intent) */}
                    <PaymentElement options={{ layout: 'accordion' }} />
                  </>
                ) : (
                  <div style={{ padding: '0.5rem 0', color: 'var(--fi-muted)', fontSize: '0.9rem' }}>
                    Preparing secure payment...
                  </div>
                )}
              </div>
            </div>

            {/* FIX #9: Disable submit button during processing or after success to prevent duplicate payments */}
            {/* FIX #24: Add ARIA labels for accessibility */}
            <button 
              className={styles.submitButton}
              type="submit" 
              disabled={!stripe || processing || success || !clientSecret}
              aria-label={processing 
                ? (paymentIntentCreated ? 'Processing payment' : 'Preparing payment') 
                : (paymentIntentCreated ? `Pay $${(totalPrice || 0).toFixed(2)}` : 'Continue to payment')
              }
              aria-busy={processing}
            >
              {processing ? (
                <>
                  <div className={styles.loadingSpinner} />
                  {paymentIntentCreated ? 'Processing Payment...' : 'Preparing Payment...'}
                </>
              ) : (
                <>
                  <FiCreditCard size={18} />
                  {/* FIX #9: Use memoized total price instead of calling function in render */}
                  {paymentIntentCreated ? `Pay $${(totalPrice || 0).toFixed(2)}` : `Continue to Payment`}
                </>
              )}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}

// Main payment form component with Stripe Elements
function PaymentForm() {
  const { config, loading, error } = useServerConfig();
  const [clientSecret, setClientSecret] = useState(null);
  // FIX: Preserve promo code state in parent to survive Elements remounts
  const [preservedAppliedPromo, setPreservedAppliedPromo] = useState(null);
  // FIX: Track if we've ever had a clientSecret to create a stable key
  // Only remount Elements when transitioning from no-secret to secret (or vice versa)
  // This prevents remount loops when clientSecret is updated with the same value
  const hasHadClientSecretRef = useRef(false);
  
  // FIX: Use ref to store stripe promise to prevent recreation and "stripe prop change" error
  // Stripe Elements doesn't allow changing the stripe prop after it's been set
  // Note: Frontend Stripe.js SDK automatically uses the latest compatible version
  // The backend API version (2025-10-29.clover) is configured in server/config/stripe.js
  // and applies to all backend API calls
  // FIX: Use useMemo to create stripe promise - same pattern as DonationForm
  // This ensures the promise is stable and only recreates when config changes
  // Extract key to avoid dependency on entire config object
  const stripeKey = config?.stripePublishableKey;
  const stripePromise = useMemo(() => {
    if (!stripeKey) return null;
    
    try {
      return loadStripe(stripeKey);
    } catch (stripeError) {
      logger.error('Failed to load Stripe', { error: stripeError, hasKey: !!stripeKey });
      return null;
    }
  }, [stripeKey]); // Only depend on the key value, not entire config object

  // FIX: Track clientSecret transitions in useEffect to avoid modifying refs during render
  useEffect(() => {
    if (clientSecret && !hasHadClientSecretRef.current) {
      hasHadClientSecretRef.current = true;
    }
  }, [clientSecret]);

  // FIX: Use clientSecret as key to ensure Elements remounts when payment intent changes
  // This is critical for ensuring Stripe Elements uses the correct payment intent amount
  // when promo codes are applied and payment intents are recreated
  // IMPORTANT: Must be called before any early returns to follow React Hooks rules
  const elementsKey = useMemo(() => {
    // Use clientSecret as key to force remount when it changes
    // This ensures Stripe Elements always uses the latest payment intent
    if (clientSecret) {
      // Extract payment intent ID from clientSecret for stable key
      // Format: pi_xxx_secret_yyy -> use pi_xxx as key
      const match = clientSecret.match(/^(pi_[^_]+)/);
      if (match) {
        return `payment-intent-${match[1]}`;
      }
      // Fallback: use clientSecret hash or full value (less stable but works)
      return `payment-intent-${clientSecret.substring(0, 20)}`;
    } else {
      // No secret - initial state
      return 'init';
    }
  }, [clientSecret]); // Remount when clientSecret changes (including on forceRecreate)

  const handleClientSecretChange = useCallback((newClientSecret) => {
    logger.debug('Client secret changed', { hasValue: !!newClientSecret, current: !!clientSecret, isSame: newClientSecret === clientSecret });
    // FIX: Only update if value actually changed to prevent unnecessary remounts
    // CRITICAL: This prevents remount loops - only update if it's actually a different value
    if (newClientSecret !== clientSecret) {
      setClientSecret(newClientSecret);
    } else {
      logger.debug('Skipping clientSecret update - value unchanged', { hasValue: !!newClientSecret });
    }
  }, [clientSecret]);

  // Helper function to get computed CSS variable value
  // CRITICAL: Must be defined before useMemo to avoid errors
  const getCSSVariable = useCallback((variableName) => {
    try {
      if (typeof window !== 'undefined' && window.document) {
        const root = document.documentElement;
        const value = getComputedStyle(root).getPropertyValue(variableName).trim();
        if (value) return value;
      }
    } catch (error) {
      // Silently fall back to defaults if getComputedStyle fails
      logger.debug('Failed to get CSS variable', { variableName, error });
    }
    // Fallback values for SSR or when getComputedStyle fails
    const fallbacks = {
      '--primary-color': '#D8B889',
      '--background-primary': '#FFFFFF',
      '--text-primary': '#1A202C',
      '--text-secondary': '#4A5568',
      '--error-color': '#E53E3E',
      '--border-color': '#E2E8F0'
    };
    return fallbacks[variableName] || '#D8B889';
  }, []);
  
  // FIX: Memoize appearance object to prevent Stripe Elements from thinking options changed
  // IMPORTANT: Must be called before any early returns to follow React Hooks rules
  const appearance = useMemo(() => ({
    theme: 'stripe',
    variables: {
      colorPrimary: getCSSVariable('--primary-color'),
      colorBackground: getCSSVariable('--background-primary'),
      colorText: getCSSVariable('--text-primary'),
      colorTextSecondary: getCSSVariable('--text-secondary'),
      colorDanger: getCSSVariable('--error-color'),
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
      borderRadius: 0
    },
    rules: {
      '.Input, .Tab, .Block': { borderRadius: 0, borderColor: getCSSVariable('--border-color') },
      '.Input:focus, .Tab:focus': { boxShadow: '0 0 0 3px rgba(216,184,137,0.25)' },
      '.Label': { fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.025em' }
    }
  }), [getCSSVariable]); // getCSSVariable is stable (useCallback with empty deps)

  // FIX: Don't initialize Stripe Elements with null clientSecret - it causes IntegrationError
  // Only render Elements when we have a valid clientSecret or when we're ready to create one
  // Pass undefined instead of null to avoid Stripe validation errors
  // IMPORTANT: Must be called before any early returns to follow React Hooks rules
  const elementsOptions = useMemo(() => {
    // Only include clientSecret if it's not null
    const options = { appearance };
    if (clientSecret) {
      options.clientSecret = clientSecret;
    }
    return options;
  }, [clientSecret, appearance]);

  if (loading) {
    return (
      <div className={styles.container}>
        <motion.div
          className={styles.paymentCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            Loading payment configuration...
          </div>
        </motion.div>
      </div>
    );
  }

  if (error || !config || !config.stripePublishableKey) {
    return (
      <div className={styles.container}>
        <motion.div
          className={styles.paymentCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className={styles.errorMessage}
            role="alert"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.errorIcon}>
              <FiAlertCircle />
            </div>
            <div className={styles.errorText}>
              Stripe is not properly configured. Please check your server configuration.
              {error && <div style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>Error: {error}</div>}
            </div>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // FIX #7: Enhanced error handling for Stripe load failure
  if (!stripePromise) {
    return (
      <div className={styles.container}>
        <motion.div
          className={styles.paymentCard}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className={styles.errorMessage}
            role="alert"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.errorIcon}>
              <FiAlertCircle />
            </div>
            <div className={styles.errorText}>
              {error 
                ? `Payment system configuration error: ${error}. Please contact support.`
                : 'Stripe payment system is not available. Please refresh the page or contact support if the problem persists.'
              }
            </div>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // FIX #36: Removed logger.debug from render - use useEffect for logging if needed

  // FIX: Always render Elements wrapper (required for useStripe/useElements hooks)
  // But only include clientSecret in options when it's available (not null)
  // This prevents the "clientSecret should be a client_secret string. You specified: null" error
  return (
    <Elements key={elementsKey} stripe={stripePromise} options={elementsOptions}>
      <PaymentFormContent 
        onClientSecretChange={handleClientSecretChange} 
        clientSecretFromParent={clientSecret}
        preservedAppliedPromo={preservedAppliedPromo}
        setPreservedAppliedPromo={setPreservedAppliedPromo}
      />
    </Elements>
  );
}

export default PaymentForm;
